import { ToastContext } from "context/ToastContext";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import {
  Fragment,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetAllBaseRateFilesQuery } from "store/queries/baseRateFiles";
import {
  DraftRunStatus,
  DraftVerdict,
  IContractOnFile,
  IExistingBaseRateFile,
  IDraftRecMatch,
  IDraftRecommendation,
  IDraftVerification,
  useAcceptContractModelDraftRecommendationMutation,
  useApplyContractModelDraftContractTermMutation,
  useCancelContractModelDraftRunMutation,
  useConfirmContractModelDraftDocumentMutation,
  useContinueContractModelDraftRunMutation,
  useDeleteContractModelDraftRunMutation,
  useGetContractModelDraftRunQuery,
  useGetContractModelDraftRunProgressQuery,
  useGetContractModelDraftExistingBaseRateFilesQuery,
  useGetContractModelDraftProviderHintQuery,
  useGetContractModelDraftVerificationQuery,
  useLazyGetContractModelDraftBaseRateFileCsvQuery,
  usePresignContractModelDraftDocumentMutation,
  useChatContractModelDraftRunMutation,
  useDeleteContractModelDraftMessageMutation,
  useRegenerateContractModelDraftRunMutation,
  useRejectContractModelDraftRecommendationMutation,
  useRestoreContractModelDraftRecommendationMutation,
  useRunContractModelDraftGenerationMutation,
  useSetContractModelDraftRecommendationImplementedMutation,
  useStopContractModelDraftPassMutation,
} from "store/queries/contractModelDrafts";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";

import {
  BACK_TO_CONTRACT,
  BACK_TO_CONTRACT_MODELS,
  CANCEL_MODEL,
  CANCEL_MODEL_FAILED,
  CANCEL_MODEL_HEADER,
  CANCEL_MODEL_MESSAGE,
  CONTRACT_MODEL,
  DELETE_MODEL,
  DELETE_MODEL_FAILED,
  DELETE_MODEL_HEADER,
  DELETE_MODEL_MESSAGE,
  effectiveDateMissingBanner,
  enterEffectiveDateOnCard,
  LOADING_CONTRACT_MODEL,
  MODEL_CANCELED,
  MODEL_DELETED,
  modelIdLabel,
  PICK_A_CONTRACT,
  pickContractAttention,
  payerIdTitle,
  providerIdTitle,
  QUEUED_TOAST_DETAIL,
  REQUEST_CHANGE_QUEUED_HINT,
  RETRY_FAILED,
  RETRY_STARTED,
  RUN_NOTHING_MODELED,
  SAVE_DRAFT_FAILED,
  SAVE_DRAFT_TOOLTIP,
  SCHEDULE_ADDED_CHIP,
  STEP_BUILDING,
  STOP_FAILED,
  termsNeedEffectiveDate,
  UNKNOWN_PAYER,
  UNKNOWN_PROVIDER,
  updatedChip,
} from "./copy";
import { RATES_STAGE_ENABLED } from "./featureFlags";
import GenerationProgress from "./GenerationProgress";
import {
  IRunChangeKey,
  PROGRESS_POLL_MS,
  RUN_SAFETY_REFETCH_MS,
  freshProgress,
  liveRunOf,
  progressChangeKey,
  runChangeKey,
  shouldRefetchRun,
} from "./livePolling";
import {
  PayerNameResolver,
  humanizeRunPayerRefs,
  mergePayerNames,
  namePayerGroups,
  payerNameResolver,
  providerNameResolver,
} from "./payerNames";
import { useLiveProgress } from "./progress/useLiveProgress";
import {
  inPlaceRevision,
  isFailedRun,
  retryModeOf,
  runHasChanges,
  showsConversation,
  showsGenerateRow,
} from "./runState";
import {
  UPLOAD_ACCEPT,
  isAllowedUpload,
  unsupportedFileDetail,
  uploadErrorDetail,
  uploadRunDocument,
} from "./uploadDocument";
import {
  attributionBadge,
  canGenerateBaseRateFile,
  collectMissingInputs,
  comparisonSummary,
  componentTypeLabel,
  confidenceSeverity,
  effectiveDateBlockedTerms,
  firstPayerRulesHref,
  generateHint,
  generationBlockers,
  generationHelpText,
  groupByPayer,
  humanizeKey,
  buildSteps,
  buildStepsNote,
  canCancelRun,
  canDeleteRun,
  isEffectiveDateBlocker,
  isGeneratableAction,
  isOnChecklist,
  isProcessing,
  isRunFinished,
  LIVE_META_KEYS,
  looksLikeMissingDocument,
  missingInputsOf,
  openQuestionsOf,
  payerRulesHref,
  productionFileNames,
  recPayer,
  recStatusLabel,
  recSubtitle,
  reuseBaseRateFileIdToFill,
  runDisplayName,
  runStatusLabel,
  runStatusSeverity,
  siteTierLabel,
} from "./utils";
import AdjustmentRulesPanel from "./wizard/AdjustmentRulesPanel";
import ApplyContractTermPanel from "./wizard/ApplyContractTermPanel";
import { SAVE_DRAFT } from "./wizard/applyTermPreview";
import ChangesScreen from "./wizard/ChangesScreen";
import {
  SAVED_ON_CONTRACT,
  onFileIsTermsProduction,
} from "./wizard/contractOnFile";
import ContractOnFileCard, {
  ContractOnFileValues,
} from "./wizard/ContractOnFileCard";
import ConversationPanel, {
  ChatLayout,
  ChatMain,
  StepRailColumn,
  WizardColumn,
} from "./wizard/ConversationPanel";
import {
  IScheduleExtraction,
  extractingSlotKeys,
  isInterimRun,
  planStatusFor,
  rebaseDraft,
  showsScheduleAddedChip,
  stepsStillBuilding,
  updatedChipLabels,
} from "./wizard/factsFirst";
import { GenerationStepPreview } from "./wizard/GenerationPreview";
import LineOfBusinessPicker from "./wizard/LineOfBusinessPicker";
import {
  partialPreviewOf,
  showSegmentsWhileGenerating,
} from "./wizard/partialPreview";
import PendingChangesBar from "./wizard/PendingChangesBar";
import ProposedModelingForm, {
  IPayerOption,
  liveValidationErrors,
} from "./wizard/ProposedModelingForm";
import RateGrid from "./wizard/RateGrid";
import {
  ISaveDraftSummary,
  runSaveDraft,
  saveDraftCandidateCount,
  saveDraftSkips,
  saveDraftTerms,
  summarizeSaveDraft,
} from "./wizard/saveDraft";
import ServiceLinePicker from "./wizard/ServiceLinePicker";
import {
  ILockedStageRow,
  ratesStageEntries,
  recommendationsForStep,
  serviceLineOf,
  serviceLinesIn,
  stepBlocksAdvance,
  stepCanConfirm,
  stepForRecommendation,
  StepId,
  stepIsConfirmed,
  unlockedThroughIndex,
  unresolvedInStep,
  visibleSteps,
  WIZARD_STEPS,
} from "./wizard/steps";

/** Pull a human-readable message out of an RTK Query error, a thrown Error, or anything else. */
const errorDetail = (e: unknown): string => {
  const apiMessage = (e as { data?: { message?: string } })?.data?.message;
  if (typeof apiMessage === "string" && apiMessage.length > 0)
    return apiMessage;
  if (e instanceof Error && e.message) return e.message;
  return "Could not upload the file.";
};

/**
 * resolve(key, value, fallbackName) returns the display name for a payer or provider id (payerId 20 →
 * "Aetna"), and null for any other key. CLE-2811 (2026-09-28) — Ani: "Instead of id show the name": for a
 * payer or provider it never returns the id — an unresolvable one is "Unknown payer" (`payerNames.ts`).
 * `fallbackName` is the name the agent wrote beside the id.
 */
type IdResolver = (
  key: string,
  value: unknown,
  fallbackName?: string | null
) => string | null;

/** A key that holds a payer id (or a list of them): payerId, medicareAdvantagePayerId, payerIds. */
const isPayerKey = (key: string): boolean => /payerids?$/i.test(key);
/** A key that holds a provider id (or a list of them). */
const isProviderKey = (key: string): boolean => /providerids?$/i.test(key);

/** The tooltip that keeps an id reachable for a super admin (the tool is super-admin only). */
const idTitle = (key: string, id: unknown): string | undefined => {
  if (isPayerKey(key)) return payerIdTitle(id);
  if (isProviderKey(key)) return providerIdTitle(id);
  return undefined;
};

const agentName = (v: unknown): string | null =>
  typeof v === "string" && v.trim() !== "" ? v : null;

// Long arrays (a carveout's ~60 CPT rows) collapse to a preview with a "show all" toggle so the card
// stays navigable.
const COLLAPSE_THRESHOLD = 8;
function CollapsibleRows({ children }: { children: ReactNode[] }) {
  const [expanded, setExpanded] = useState(false);
  if (children.length <= COLLAPSE_THRESHOLD)
    return <div className="flex flex-column gap-2">{children}</div>;
  const shown = expanded ? children : children.slice(0, COLLAPSE_THRESHOLD);
  return (
    <div className="flex flex-column gap-2">
      {shown}
      <Button
        className="p-button-text p-button-sm p-0 align-self-start"
        label={
          expanded
            ? "Show fewer"
            : `Show all ${children.length} (${
                children.length - COLLAPSE_THRESHOLD
              } more)`
        }
        onClick={() => setExpanded((v) => !v)}
      />
    </div>
  );
}

// Render one proposedModeling value: primitives inline, arrays/objects as nested readable rows — so the
// reviewer reads labeled fields instead of a horizontally-scrolling JSON blob. The label column is
// fit-to-content (grid `max-content 1fr`), so values sit next to their labels rather than far right.
function ProposedValue({
  value,
  fieldKey,
  resolve,
}: {
  value: unknown;
  fieldKey?: string;
  resolve?: IdResolver;
}) {
  if (value === null || value === undefined) {
    return <span className="text-700">&mdash;</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-700">(none)</span>;
    const allPrimitive = value.every(
      (v) => typeof v !== "object" || v === null
    );
    if (allPrimitive) {
      return (
        <ul className="mt-0 mb-0 pl-3">
          {value.map((v, i) => {
            // A list of payer/provider ids reads as their names (CLE-2811).
            const name = fieldKey && resolve ? resolve(fieldKey, v) : null;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <li
                key={i}
                title={name && fieldKey ? idTitle(fieldKey, v) : undefined}
              >
                {name ?? String(v)}
              </li>
            );
          })}
        </ul>
      );
    }
    return (
      <CollapsibleRows>
        {value.map((v, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="surface-50 border-round p-2">
            <ProposedValue value={v} resolve={resolve} />
          </div>
        ))}
      </CollapsibleRows>
    );
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    // Collapse the payer/provider id+name pairs into a single readable row: "Payer  Aetna" and
    // "Provider  HMA (Specialist Facility rates)" — no separate id and name rows. CLE-2811 (2026-09-28) —
    // Ani: "Instead of id show the name": the name alone; the id is the row's tooltip.
    const keys = Object.keys(obj).map((k) => k.toLowerCase());
    const hasPayerId = keys.includes("payerid");
    const hasProviderId = keys.includes("providerid");
    const entries = Object.entries(obj).filter(
      ([k]) =>
        !(hasPayerId && k.toLowerCase() === "payername") &&
        !(hasProviderId && k.toLowerCase() === "providername") &&
        !LIVE_META_KEYS.includes(k)
    );
    const named = (
      label: string,
      name: string,
      id: unknown,
      title: string
    ): [string, ReactNode] => [
      label,
      id === null || id === undefined || id === "" ? (
        <span className="text-700">&mdash;</span>
      ) : (
        <span style={{ overflowWrap: "anywhere" }} title={title}>
          {name}
        </span>
      ),
    ];
    return (
      // NOT PrimeFlex `.grid` — that class carries negative margins that pull the block up into any
      // inline text before it (e.g. the "It originally proposed:" line), which read as overlap.
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "max-content 1fr",
          columnGap: "1.5rem",
          rowGap: "0.25rem",
          alignItems: "start",
        }}
      >
        {entries.map(([k, v]) => {
          const kl = k.toLowerCase();
          let label = humanizeKey(k);
          let node: ReactNode = (
            <ProposedValue value={v} fieldKey={k} resolve={resolve} />
          );
          if (kl === "payerid") {
            const fallback = agentName(obj.payerName);
            const nm =
              (resolve ? resolve("payerId", v, fallback) : fallback) ??
              UNKNOWN_PAYER;
            [label, node] = named("Payer", nm, v, payerIdTitle(v));
          } else if (kl === "providerid") {
            const fallback = agentName(obj.providerName);
            const nm =
              (resolve ? resolve("providerId", v, fallback) : fallback) ??
              UNKNOWN_PROVIDER;
            [label, node] = named("Provider", nm, v, providerIdTitle(v));
          }
          return (
            <Fragment key={k}>
              <div className="font-medium text-700 white-space-nowrap">
                {label}
              </div>
              <div style={{ overflowWrap: "anywhere", minWidth: 0 }}>
                {node}
              </div>
            </Fragment>
          );
        })}
      </div>
    );
  }
  // An id under a payer/provider key (medicareAdvantagePayerId: 20) reads as the name alone (CLE-2811).
  const name = fieldKey && resolve ? resolve(fieldKey, value) : null;
  return (
    <span
      style={{ overflowWrap: "anywhere" }}
      title={name && fieldKey ? idTitle(fieldKey, value) : undefined}
    >
      {name ?? String(value)}
    </span>
  );
}

function AttributionBadges({ rec }: { rec: IDraftRecommendation }) {
  if (rec.attributions.length === 0) {
    return <span className="text-700 text-sm">No source attribution</span>;
  }
  return (
    <div className="flex flex-column gap-3">
      {rec.attributions.map((a) => {
        const badge = attributionBadge(a);
        return (
          <div key={a.id} className="flex flex-column gap-1">
            {/* Icon + text, not a filled pill: a page with six loud red/green tags on it reads as an
                error state even when nothing is wrong. */}
            <span
              title={badge.tip}
              className="inline-flex align-items-center gap-1 text-sm font-medium"
              style={{ color: badge.color }}
            >
              <i
                className={`pi ${badge.icon}`}
                style={{ fontSize: "0.85rem" }}
              />
              {badge.label}
            </span>
            <blockquote className="m-0 pl-3 border-left-2 surface-border text-sm">
              <em>&ldquo;{a.exactQuote}&rdquo;</em>
            </blockquote>
            {a.fileName || a.locator ? (
              <span className="text-600 text-xs">
                Source: {[a.fileName, a.locator].filter(Boolean).join(" — ")}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// Per-recommendation verdict: how THIS rec lines up with what's already in production. Rendered as an
// icon + colored text (not a filled pill) so the confidence pill stays the only badge on a row, and so
// the states that need attention — amber "needs review", red "differs" — stand out from the calm ones.
const VERDICT_META: Record<
  string,
  { icon: string; color: string; label: string; tip: string }
> = {
  match: {
    icon: "pi-check-circle",
    color: "var(--green-600)",
    label: "Matches production",
    tip: "A matching construct exists in production and the value we can compare agrees.",
  },
  differs: {
    icon: "pi-ban",
    color: "var(--red-600)",
    label: "Differs from production",
    tip: "A matching construct exists, but a value we can compare disagrees with the proposal.",
  },
  present: {
    icon: "pi-eye",
    color: "var(--orange-600)",
    // Was "Needs review", which collided word-for-word with the review-status tag. This says what it
    // actually means: the thing exists in production, but we could not compare its values for you.
    label: "In production — check the values",
    tip: "A construct of this type exists for this provider & payer, but its exact values weren't auto-compared — confirm they match the proposal.",
  },
  missing: {
    icon: "pi-minus-circle",
    color: "var(--text-color-secondary)",
    label: "Not in production",
    tip: "Nothing in production corresponds to this recommendation yet — it still needs to be built.",
  },
};

function VerdictBadge({ verdict }: { verdict?: DraftVerdict }) {
  if (!verdict || verdict === "n_a") return null;
  const m = VERDICT_META[verdict];
  return (
    <span
      title={m.tip}
      className="inline-flex align-items-center gap-1 font-semibold text-sm white-space-nowrap"
      style={{ color: m.color }}
    >
      <i className={`pi ${m.icon}`} style={{ fontSize: "0.9rem" }} />
      {m.label}
    </span>
  );
}

// A deep link straight to the live construct in the Payer Rules editor, opened in a new tab so the
// checklist stays put. Shown only when the construct has a navigable page.
function PayerRulesLink({ href }: { href: string }) {
  return (
    <Link
      to={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex align-items-center gap-1 text-sm font-medium no-underline"
      style={{ color: "var(--primary-color)" }}
    >
      <i className="pi pi-external-link" style={{ fontSize: "0.75rem" }} />
      Check it out
    </Link>
  );
}

/** Production rows the compact "In production now" block shows before folding the rest away. */
const PRODUCTION_ROWS_SHOWN = 2;

/**
 * CLE-2811 — what production already has for this recommendation, shown ABOVE the proposed form and
 * kept compact: the note rides on the title line, the rows sit side by side where the width allows,
 * and past two rows the rest fold behind "Show N more".
 *
 * It used to be the right half of a two-column grid. With the conversation docked beside the wizard
 * that squeezed the editable form into half of what was left, and its date inputs were cut off.
 */
function InProductionNow({
  componentType,
  match,
  resolve,
}: {
  componentType: IDraftRecommendation["componentType"];
  match: IDraftRecMatch;
  resolve?: IdResolver;
}) {
  const [expanded, setExpanded] = useState(false);
  const { rows } = match;
  const shown = expanded ? rows : rows.slice(0, PRODUCTION_ROWS_SHOWN);
  return (
    <section aria-label="In production now" className="mb-3">
      <div className="flex flex-wrap align-items-baseline column-gap-2 mb-1">
        <span className="text-sm font-semibold">In production now</span>
        {match.verdict === "present" && rows.length > 0 ? (
          <span className="text-600 text-xs">
            A matching construct exists; its values weren&rsquo;t compared
            automatically &mdash; confirm they match.
          </span>
        ) : null}
      </div>
      <div className="surface-50 p-2 border-round text-sm">
        {rows.length === 0 ? (
          <span className="text-700">
            No matching {componentTypeLabel(componentType)} found for this
            provider &amp; payer.
          </span>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {shown.map((row, i) => {
                const href = payerRulesHref(componentType, row);
                return (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    className="surface-0 border-round p-2"
                    // Two to a line once each can have 20rem; one per line below that. A basis rather
                    // than a min-width, so a phone-width card shrinks instead of overflowing.
                    style={{ flex: "1 1 20rem", minWidth: 0 }}
                  >
                    <ProposedValue value={row} resolve={resolve} />
                    {href ? (
                      <div className="mt-2">
                        <PayerRulesLink href={href} />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            {rows.length > PRODUCTION_ROWS_SHOWN ? (
              <Button
                type="button"
                className="p-button-text p-button-sm p-0 mt-2"
                label={
                  expanded
                    ? "Show fewer"
                    : `Show ${rows.length - PRODUCTION_ROWS_SHOWN} more`
                }
                onClick={() => setExpanded((v) => !v)}
              />
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

/**
 * CLE-2811 (2026-09-28) — on a run tied to a contract, the contract term's "In production now" is that
 * contract as it stands (`contractOnFile`, read live on every fetch): the same values the Contract Terms
 * step folds under "Saved on this contract", instead of the verification's raw row dump.
 */
function InProductionOnFile({ contract }: { contract: IContractOnFile }) {
  return (
    <section aria-label="In production now" className="mb-3">
      <div className="flex flex-wrap align-items-baseline column-gap-2 mb-1">
        <span className="text-sm font-semibold">In production now</span>
        <span className="text-600 text-xs">{SAVED_ON_CONTRACT}</span>
      </div>
      <div className="surface-50 p-2 border-round text-sm">
        <ContractOnFileValues contract={contract} />
        <div className="mt-2">
          <PayerRulesLink
            href={`/payer-rules/contracts/${contract.contractFeeScheduleId}`}
          />
        </div>
      </div>
    </section>
  );
}

// The "In production" cell for one checklist row: the verdict plus a one-line, human-readable detail.
function ProductionCell({
  rec,
  match,
  loading,
}: {
  rec: IDraftRecommendation;
  match?: IDraftRecMatch | null;
  loading: boolean;
}) {
  const verdict = match?.verdict;
  if (!verdict) {
    return (
      <span className="text-700 text-sm">
        {loading ? (
          <>
            <i className="pi pi-spin pi-spinner mr-1" />
            Checking…
          </>
        ) : (
          "—"
        )}
      </span>
    );
  }
  if (verdict === "n_a") {
    return (
      <span
        className="text-700 text-sm"
        title="A scoping/routing decision with no single production construct to compare against."
      >
        Not applicable
      </span>
    );
  }

  const { proposed, production } = comparisonSummary(rec, match);
  let detail: ReactNode = null;
  if (verdict === "differs") {
    detail =
      proposed && production ? (
        <div className="text-sm mt-1">
          Proposed <strong>{proposed}</strong> &middot; Production{" "}
          <strong>{production}</strong>
        </div>
      ) : (
        <div className="text-700 text-sm mt-1">
          See the recommendation below for the difference.
        </div>
      );
  } else if (verdict === "match") {
    detail = production ? (
      <div className="text-700 text-sm mt-1">Production: {production}</div>
    ) : null;
  } else if (verdict === "present") {
    const files = productionFileNames(match);
    detail =
      files.length > 0 ? (
        <div className="text-700 text-sm mt-1">
          A base rate file for this provider &amp; payer already exists (
          <strong>{files.join(", ")}</strong>), but the proposal names a
          different one &mdash; we haven&rsquo;t verified it&rsquo;s the same
          file. Confirm below.
        </div>
      ) : (
        <div className="text-700 text-sm mt-1">
          Exists in production, but its values weren&rsquo;t auto-checked
          &mdash; confirm they match below.
        </div>
      );
  } else if (verdict === "missing") {
    detail = (
      <div className="text-700 text-sm mt-1">Nothing built for this yet.</div>
    );
  }

  const href = firstPayerRulesHref(rec.componentType, match?.rows);
  return (
    <div>
      <div className="flex align-items-center flex-wrap gap-3">
        <VerdictBadge verdict={verdict} />
        {href ? <PayerRulesLink href={href} /> : null}
      </div>
      {detail}
    </div>
  );
}

// The base rate files a live contract already prices this provider+payer against — surfaced so the
// reviewer can reuse one instead of uploading a freshly generated file. Deliberately coarse (names +
// row counts, not an exact content diff): a byte-for-byte match against a hand-built production file
// almost never lands, and "here's what already exists" is the more honest, useful signal.
function ExistingBaseRateFiles({ files }: { files: IExistingBaseRateFile[] }) {
  if (files.length === 0) return null;
  return (
    <div className="mb-3 surface-50 border-round p-3">
      <div className="text-sm font-medium mb-1">
        A base rate file for this provider &amp; payer may already exist
      </div>
      <div className="text-600 text-xs mb-2">
        These are already loaded for this payer &amp; provider — you may be able
        to reference one instead of uploading a new file. Compare its row count
        to the file this would generate as a rough check.
      </div>
      <ul className="mt-0 mb-0 pl-3 text-sm text-700">
        {files.map((f) => (
          <li key={f.displayName} className="mb-1">
            <span className="font-medium">{f.displayName}</span>
            {f.rowCount != null ? ` — ${f.rowCount.toLocaleString()} rows` : ""}
            {f.effectiveDate ? ` (eff ${f.effectiveDate})` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Exported for its render test (the production-first layout); the page is the only other caller.
export function RecommendationCard({
  rec,
  onAccept,
  onReject,
  onOverride,
  busy,
  resolve,
  payers,
  baseRateFiles,
  baseRateFileOptions,
  existingBaseRateFiles = [],
  match,
  focused,
  onGenerateCsv,
  generatingCsv,
  scheduleExtraction = null,
  contractOnFile = null,
  onDraftChange,
  attention = null,
}: {
  rec: IDraftRecommendation;
  /** `edited` is sent only when the reviewer actually changed something. */
  onAccept: (edited?: Record<string, unknown>) => void;
  onReject: () => void;
  onOverride: () => void;
  busy: boolean;
  resolve?: IdResolver;
  payers?: IPayerOption[];
  /** Base rate file display names for this account — options for the "file to take a % of" picker. */
  baseRateFiles?: string[];
  /** Loaded files as {id, displayName} — so a reuse_existing pick writes both name and id. */
  baseRateFileOptions?: { id: string; displayName: string }[];
  /** Base rate files already in production for this run's provider+payer — a possible reuse. */
  existingBaseRateFiles?: IExistingBaseRateFile[];
  match?: IDraftRecMatch | null;
  focused?: boolean;
  onGenerateCsv?: (
    recId: string,
    proposedModeling?: Record<string, unknown>
  ) => void;
  generatingCsv?: boolean;
  /**
   * CLE-2811 (facts first) — the run's status and `interimAt`, and this term's plan status from the
   * worker. In the facts-first window an EMPTY reimbursement slot renders as "Extracting the schedule…"
   * with the plan's chip instead of an empty box (`extractingSlotKeys`); once the pass completes, it is
   * the normal editable field. Null for anything but a contract term.
   */
  scheduleExtraction?: IScheduleExtraction | null;
  /**
   * CLE-2811 (2026-09-28) — the contract the run models, as it stands. A contract term for that contract's
   * payer shows it as "In production now" (`onFileIsTermsProduction`).
   */
  contractOnFile?: IContractOnFile | null;
  /**
   * CLE-2811 (2026-09-28) — told her unsaved edits whenever they change (null once there are none, and on
   * unmount), so "Save draft" can accept each term with the values she sees. Must be stable.
   */
  onDraftChange?: (
    recId: string,
    edits: Record<string, unknown> | null
  ) => void;
  /** CLE-2811 — why "Save draft" skipped this term, highlighted until the next Save draft. */
  attention?: string | null;
}) {
  const proposed = useMemo(
    () => (rec.proposedModeling ?? {}) as Record<string, unknown>,
    [rec.proposedModeling]
  );
  const [draft, setDraft] = useState<Record<string, unknown>>(proposed);

  // When the server sends a new value (accept, a refetch, a schedule arriving in the facts-first window),
  // take it wherever she has not edited and keep her edits where she has (`rebaseDraft`, a three-way merge
  // against the value the draft started from). With no edits that is simply the new value. The card is
  // keyed by recommendation id, and the id survives the refetch, so this state does too.
  const draftBaseRef = useRef(proposed);
  useEffect(() => {
    const base = draftBaseRef.current;
    if (base === proposed) return;
    draftBaseRef.current = proposed;
    setDraft((current) => rebaseDraft(base, proposed, current));
  }, [proposed]);

  const extractingKeys = scheduleExtraction
    ? extractingSlotKeys(rec.componentType, proposed, scheduleExtraction)
    : [];

  // The recommendation as it stands with unsaved edits — so the generate button reflects what you're
  // editing RIGHT NOW (enabled/blocked live), and generates from the draft without needing a save first.
  const draftRec = useMemo(
    () => ({ ...rec, proposedModeling: draft }),
    [rec, draft]
  );

  // Keep a reuse_existing file's required `baseRateFileId` in step with the chosen NAME. The id gets
  // stripped whenever the rec is saved under another action, so switching back to "reuse" can leave a
  // name with no id — looks selected, but won't save. Re-derive the missing id from the loaded-file list.
  useEffect(() => {
    const fill = reuseBaseRateFileIdToFill(draft, baseRateFileOptions ?? []);
    if (fill) setDraft((d) => ({ ...d, baseRateFileId: fill }));
  }, [draft, baseRateFileOptions]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(proposed),
    [draft, proposed]
  );

  // Her unsaved edits, for "Save draft" (CLE-2811). Cleared on unmount: a card that is gone has no draft.
  useEffect(() => {
    onDraftChange?.(rec.id, dirty ? draft : null);
  }, [onDraftChange, rec.id, dirty, draft]);
  useEffect(() => () => onDraftChange?.(rec.id, null), [onDraftChange, rec.id]);

  // The payer she reads on the card — a name, never the id (CLE-2811, Ani 2026-09-28).
  const termPayer = recPayer(rec);
  let payerLabel: string | null = termPayer.name;
  if (termPayer.id != null)
    payerLabel =
      resolve?.("payerId", termPayer.id, termPayer.name) ??
      termPayer.name ??
      UNKNOWN_PAYER;
  const onFile =
    rec.componentType === "contract_term" &&
    onFileIsTermsProduction(contractOnFile, termPayer.id)
      ? contractOnFile
      : null;

  // The server's field errors that still apply to the values on the card: one on a field she has changed since
  // is dropped as she types (the server re-validates on confirm), so "Required" under a date she has just
  // entered goes at once.
  const errors = useMemo(
    () => liveValidationErrors(rec.validationErrors, proposed, draft),
    [rec.validationErrors, proposed, draft]
  );
  const hasErrors = errors.length > 0;
  // CLE-2811 — the documents don't state the effective date: she types it here. Its own banner — the agent did
  // not get a value wrong, there was none to read: the proposal has no date, and the pipeline's blocker says so or
  // the only error left is on the date. A stored date never shows it. Any other field error keeps the usual banner.
  const waitsForEffectiveDate =
    proposed.effectiveDate == null &&
    (missingInputsOf(rec).some(isEffectiveDateBlocker) ||
      (hasErrors && errors.every((e) => e.path === "effectiveDate")));
  // The card's "Blocked" list: every blocker but the effective date's, which the banner above covers.
  const hardBlockers = missingInputsOf(rec).filter(
    (m) => !isEffectiveDateBlocker(m)
  );
  const hasOtherErrors = errors.some((e) => e.path !== "effectiveDate");
  const onFileEffectiveDate =
    typeof onFile?.effectiveDate === "string" &&
    /^\d{4}-\d{2}-\d{2}/.test(onFile.effectiveDate)
      ? onFile.effectiveDate.slice(0, 10)
      : null;

  // CLE-2811 (in place) — what a chat change moved on this term, for the amber "Updated: …" chip.
  const updatedLabels = updatedChipLabels(rec);

  // Current verdict, so the buttons can show a selected state on a card you're revisiting.
  const accepted = rec.status === "accepted";
  const rejected = rec.status === "rejected";

  return (
    <div
      id={`rec-${rec.id}`}
      style={{
        scrollMarginTop: "1rem",
        borderRadius: "6px",
        transition: "box-shadow 0.3s ease",
        // eslint-disable-next-line no-nested-ternary
        boxShadow: attention
          ? "0 0 0 2px var(--orange-400, #fb923c)"
          : focused
          ? "0 0 0 2px var(--primary-color)"
          : "none",
      }}
    >
      <Card className="mb-3">
        {attention ? (
          <Message
            severity="warn"
            className="w-full justify-content-start mb-3"
            text={attention}
          />
        ) : null}
        <div className="flex justify-content-between align-items-start gap-2 mb-2">
          <div className="flex align-items-center flex-wrap gap-2">
            <span className="font-bold">
              {componentTypeLabel(rec.componentType)}
            </span>
            {payerLabel ? (
              <span
                title={
                  termPayer.id != null ? payerIdTitle(termPayer.id) : undefined
                }
              >
                <Tag
                  className="white-space-nowrap"
                  icon="pi pi-building"
                  value={payerLabel}
                />
              </span>
            ) : null}
            <Tag
              severity={confidenceSeverity(rec.confidence)}
              value={`${rec.confidence} confidence`}
            />
            {/* Only worth a tag once she has ACTED on it. "Needs review" on every untouched card is
                noise — the Confirm/Reject buttons already say that, and it collided with the verdict. */}
            {rec.status !== "pending" ? (
              <Tag value={recStatusLabel(rec.status)} />
            ) : null}
            {/* CLE-2811 (facts first) — the schedule arrived after she confirmed the facts, which put the
                term back to pending: say why it needs her again. */}
            {showsScheduleAddedChip(rec) ? (
              <Tag
                className="white-space-nowrap"
                severity="warning"
                icon="pi pi-refresh"
                value={SCHEDULE_ADDED_CHIP}
                title={rec.changeReason ?? undefined}
              />
            ) : null}
            {/* CLE-2811 (in place) — a chat change moved this term (same card, new values): say what moved,
                until she confirms or rejects it. */}
            {updatedLabels.length > 0 ? (
              <Tag
                className="white-space-nowrap"
                severity="warning"
                icon="pi pi-pencil"
                value={updatedChip(updatedLabels)}
                title={rec.changeReason ?? undefined}
              />
            ) : null}
            {/* The production verdict is NOT shown here — the "In production now" block below says the
                same thing in full, and a header badge repeating it was pure duplication. */}
          </div>
        </div>
        {/* For a base rate file, show what already exists for this provider+payer — the reviewer may be
            able to reuse one instead of uploading a new file. */}
        {rec.componentType === "base_rate_file" ? (
          <ExistingBaseRateFiles files={existingBaseRateFiles} />
        ) : null}
        {waitsForEffectiveDate ? (
          <div className="mb-3 p-3 border-round border-1 border-orange-500 surface-50">
            <div className="text-sm font-semibold">
              <i className="pi pi-calendar mr-1" />
              {effectiveDateMissingBanner(onFileEffectiveDate)}
            </div>
          </div>
        ) : null}
        {(waitsForEffectiveDate ? hasOtherErrors : hasErrors) ? (
          <div className="mb-3 p-3 border-round border-1 border-orange-500 surface-50">
            <div className="text-sm font-semibold mb-1">
              <i className="pi pi-exclamation-triangle mr-1" />
              The agent&rsquo;s values don&rsquo;t fit a{" "}
              {componentTypeLabel(rec.componentType)}
            </div>
            <div className="text-sm text-700">
              Correct the highlighted fields below, then confirm. Until this is
              fixed we can&rsquo;t tell you how to build it, and we can&rsquo;t
              check it against production.
            </div>
          </div>
        ) : null}

        {rec.originalModeling ? (
          <div className="mb-3 surface-50 p-2 border-round text-xs">
            {/* Label on its own line so the value grid below starts clean — a block grid inline after
                text was where the overlap showed. */}
            <div className="mb-2">
              <span className="font-semibold">You corrected the agent.</span> It
              originally proposed:
            </div>
            <ProposedValue value={rec.originalModeling} resolve={resolve} />
          </div>
        ) : null}

        {/* CLE-2811 — production first and compact, then the proposed form at the full card width. Side
            by side, the form got half of what the docked conversation leaves and its dates were cut off. */}
        {/* eslint-disable-next-line no-nested-ternary */}
        {onFile ? (
          <InProductionOnFile contract={onFile} />
        ) : match ? (
          <InProductionNow
            componentType={rec.componentType}
            match={match}
            resolve={resolve}
          />
        ) : null}

        <section aria-label="Proposed modeling">
          <div className="text-sm font-semibold mb-1">Proposed modeling</div>
          <div className="surface-100 p-3 border-round text-sm">
            <ProposedModelingForm
              componentType={rec.componentType}
              value={draft}
              validationErrors={errors}
              disabled={busy}
              // So payerId 4 renders as "Aetna (4)" instead of a bare number the reviewer can't read.
              resolve={resolve}
              payers={payers}
              baseRateFiles={baseRateFiles}
              baseRateFileOptions={baseRateFileOptions}
              extracting={
                extractingKeys.length > 0
                  ? {
                      keys: extractingKeys,
                      planStatus: scheduleExtraction?.planStatus ?? null,
                    }
                  : null
              }
              onChange={setDraft}
            />
          </div>
        </section>

        {hardBlockers.length > 0 ? (
          <div className="mt-3">
            <div className="text-sm font-semibold mb-1">
              Blocked &mdash; can&rsquo;t model this without
            </div>
            <ul className="mt-0 mb-0 pl-3 text-sm text-700">
              {hardBlockers.map((item) => (
                <li key={item} className="mb-1">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {openQuestionsOf(rec).length > 0 ? (
          <div className="mt-3">
            <div className="text-sm font-semibold mb-1">
              Open questions &mdash; modeled anyway, please confirm
            </div>
            <ul className="mt-0 mb-0 pl-3 text-sm text-600">
              {openQuestionsOf(rec).map((item) => (
                <li key={item} className="mb-1">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-3">
          <div className="text-sm font-semibold mb-1">Source language</div>
          <AttributionBadges rec={rec} />
        </div>
        {rec.override ? (
          <div className="mt-2 surface-50 p-2 border-round text-sm">
            <span className="font-semibold">
              Feedback ({rec.override.category}):
            </span>{" "}
            {rec.override.reason}
          </div>
        ) : null}
        {/* Generate & download lives right above Save, and runs off the CURRENT (unsaved) draft — so the
            loop is edit → generate → eyeball the CSV → Save & confirm, not save-first-then-scroll-up.
            Shown for any generate-type action; disabled with a reason until the spec is complete. */}
        {onGenerateCsv &&
        (isGeneratableAction(draftRec) || canGenerateBaseRateFile(draftRec)) ? (
          <div className="mt-3 surface-50 border-round p-3">
            <Button
              label="Generate & download base rate CSV"
              icon="pi pi-download"
              className="p-button-sm p-button-outlined"
              loading={generatingCsv}
              disabled={!canGenerateBaseRateFile(draftRec) || busy}
              onClick={() => onGenerateCsv(rec.id, draft)}
            />
            <div className="text-600 text-xs mt-2">
              {canGenerateBaseRateFile(draftRec) ? (
                <>
                  {generationHelpText(draftRec)} Previews from your current
                  edits &mdash; no need to save first. Then upload it as a base
                  rate file in Payer Rules and reference it from the rate
                  calculation.
                </>
              ) : (
                <>
                  Add {generationBlockers(draftRec).join(", ")} above, then this
                  button will generate the file (CPT &rarr; rate) for you to
                  download.
                </>
              )}
            </div>
          </div>
        ) : null}
        <div className="flex gap-2 mt-3 align-items-center">
          <Button
            // The buttons show the CURRENT verdict as a selected state, so you can see what you already
            // did on a card you're revisiting. Confirmed → solid green with a filled check; otherwise a
            // plain Confirm. Both stay clickable so you can change your mind.
            label={
              // eslint-disable-next-line no-nested-ternary
              dirty ? "Save & confirm" : accepted ? "Confirmed" : "Confirm"
            }
            icon={
              // eslint-disable-next-line no-nested-ternary
              dirty
                ? "pi pi-save"
                : accepted
                ? "pi pi-check-circle"
                : "pi pi-check"
            }
            className={`p-button-sm ${accepted ? "p-button-success" : ""}`}
            tooltip="Marks this as correct and adds it to the Build checklist. Immediate — no rebuild."
            tooltipOptions={{ position: "top" }}
            onClick={() => onAccept(dirty ? draft : undefined)}
            disabled={busy}
          />
          <Button
            label={rejected ? "Rejected" : "Reject"}
            icon={rejected ? "pi pi-times-circle" : undefined}
            className={`p-button-sm p-button-danger ${
              rejected ? "" : "p-button-outlined"
            }`}
            tooltip="Skip this — it won't be built. Reversible."
            tooltipOptions={{ position: "top" }}
            onClick={onReject}
            disabled={busy}
          />
          <Button
            // Same mechanism as the conversation panel — this one is just pre-scoped to this
            // recommendation. It is sent to the AI, which rebuilds the model and replies.
            label="Request a change"
            icon="pi pi-comment"
            className="p-button-sm p-button-text"
            tooltip="Tell the AI what it got wrong here. It rebuilds the model and replies in the conversation."
            tooltipOptions={{ position: "top" }}
            onClick={onOverride}
            disabled={busy}
          />
          {dirty ? (
            <small className="text-color-secondary ml-auto">
              Unsaved changes
            </small>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

/**
 * CLE-2811 — the "Continue to rates" call-to-action. A facts run stops at Contract Terms; this is where the
 * reviewer asks for everything else, once she has cleaned up the facts (and saved them to the contract).
 */
function ContinueToRatesCta({
  status,
  continuing,
  onContinue,
}: {
  status: DraftRunStatus;
  continuing: boolean;
  onContinue: () => void;
}) {
  return (
    <div className="surface-card border-1 surface-border border-round p-3 mt-3 flex align-items-center justify-content-between gap-3 flex-wrap">
      <span className="text-sm">
        Model rates, carve-outs and rules from this document. Takes several
        minutes.
      </span>
      <Button
        label="Continue to rates"
        icon="pi pi-arrow-right"
        iconPos="right"
        loading={continuing}
        disabled={continuing || isProcessing(status) || status === "canceled"}
        onClick={onContinue}
      />
    </div>
  );
}

/** The rail row's icon: still being built (CLE-2811), locked, confirmed, or still to do. */
const railIcon = (
  building: boolean,
  locked: boolean,
  confirmed: boolean
): string => {
  if (building) return "pi pi-spin pi-spinner text-primary";
  if (locked) return "pi pi-lock text-color-secondary";
  if (confirmed) return "pi pi-check-circle text-green-600";
  return "pi pi-circle text-color-secondary";
};

/**
 * The wizard's left rail. Steps lock: you may move through the ones you have settled and the one you
 * are settling, and no further (Ani's v0 — "Confirm & continue", padlocks on what's ahead).
 *
 * An earlier build deliberately left every step clickable, reasoning that a reviewer wants to skim
 * ahead. We've taken her design instead — the sequence is the method, and reviewing outpatient rates
 * before you've settled the contract's effective date invites exactly the rework the order prevents.
 *
 * Steps already passed stay open, so going *back* to fix something is always allowed. Only what's ahead
 * of the gate is locked, and the lock is on `pending` items alone — see `stepBlocksAdvance`.
 */
function StepRail({
  recs,
  activeStep,
  onSelect,
  unlockedThrough,
  visible,
  lockedRow,
  building = [],
}: {
  recs: IDraftRecommendation[];
  activeStep: StepId;
  onSelect: (step: StepId) => void;
  /** Index into WIZARD_STEPS; anything beyond this is locked. */
  unlockedThrough: number;
  /**
   * The steps this run shows (`visibleSteps`): the facts stage shows only the facts steps, Inpatient is
   * hidden for ASC/Professional, and What Changed / Lines of Business / Service Lines appear only when
   * they have something to show. The upload screen is never a rail entry.
   */
  visible: StepId[];
  /**
   * CLE-2811 — `lockedStageRow`: on a facts run, ONE locked "Rates & fee schedules" row stands for every
   * rate step the stage hides. Not a button — nothing to navigate to until "Continue to rates".
   */
  lockedRow: ILockedStageRow | null;
  /** CLE-2811 — steps the first pass is still building: a spinner instead of a (vacuous) check. */
  building?: StepId[];
}) {
  return (
    <nav className="flex flex-column gap-1">
      {WIZARD_STEPS.filter(
        (s) => s.id !== "documents" && visible.includes(s.id)
      ).map((step) => {
        const outstanding = unresolvedInStep(recs, step.id);
        const isBuilding = building.includes(step.id);
        const confirmed =
          !isBuilding &&
          stepCanConfirm(step.id) &&
          stepIsConfirmed(recs, step.id);
        const isActive = step.id === activeStep;
        const locked =
          WIZARD_STEPS.findIndex((s) => s.id === step.id) > unlockedThrough;
        return (
          <button
            key={step.id}
            type="button"
            disabled={locked}
            title={
              locked
                ? "Confirm the steps before this one to continue"
                : undefined
            }
            onClick={() => onSelect(step.id)}
            className={`text-left border-round p-3 w-full border-none ${
              locked ? "cursor-auto opacity-50" : "cursor-pointer"
            } ${isActive ? "text-primary font-medium" : "surface-card"}`}
            style={
              isActive
                ? {
                    // A grey tint was invisible against the page's grey. A clear primary-tinted fill
                    // plus a solid accent bar on the leading edge is unmistakable at a glance, which is
                    // the whole job of a rail.
                    background: "var(--primary-50, #eef2ff)",
                    borderLeft: "4px solid var(--primary-color)",
                    boxShadow: "inset 0 0 0 1px var(--primary-200, #c7d2fe)",
                  }
                : { borderLeft: "4px solid transparent" }
            }
          >
            <div className="flex align-items-center gap-2">
              <i
                className={railIcon(isBuilding, locked, confirmed)}
                title={isBuilding ? STEP_BUILDING : undefined}
                aria-label={isBuilding ? STEP_BUILDING : undefined}
              />
              <span
                className={`text-sm ${isActive ? "font-bold" : "font-medium"}`}
              >
                {step.title}
              </span>
              {outstanding > 0 ? (
                <Tag
                  className="ml-auto"
                  severity="warning"
                  value={String(outstanding)}
                />
              ) : null}
            </div>
          </button>
        );
      })}
      {lockedRow ? (
        <div
          className="border-round p-3 w-full opacity-60 cursor-auto"
          style={{ borderLeft: "4px solid transparent" }}
          aria-disabled="true"
          title="Continue to rates on the Contract Terms step opens these"
        >
          <div className="flex align-items-center gap-2">
            <i className="pi pi-lock text-color-secondary" />
            <span className="text-sm font-medium">{lockedRow.title}</span>
          </div>
          <small className="block mt-1 pl-4 text-color-secondary">
            {lockedRow.subtitle}
          </small>
        </div>
      ) : null}
    </nav>
  );
}

function ReviewExplainer({ count }: { count: number }) {
  return (
    <div className="surface-50 border-1 surface-border border-round p-3 mb-3 text-sm">
      <div className="font-semibold mb-1">How to review this</div>
      <p className="mt-0 mb-2 text-700 line-height-3">
        The model read your documents and proposed {count} pieces of a
        ClearHealth pricing configuration. Nothing here is live — reviewing it{" "}
        <strong>never changes production pricing</strong>. Your job is to say,
        for each piece, whether the model read the contract correctly.
      </p>
      <ul className="mt-0 mb-0 pl-3 text-700">
        <li className="mb-1">
          Check the <strong>source language</strong> quoted under each proposal
          &mdash; against the contract itself. Most quotes are checked against
          the text we extracted from your documents; a few can&rsquo;t be
          matched automatically (scanned pages and spreadsheets are hard to
          match), and those are flagged &mdash; a flag means &ldquo;we
          couldn&rsquo;t verify it&rdquo;, not that the quote is wrong.
        </li>
        <li className="mb-1">
          <strong>Confirm</strong> marks the proposal as agreed, so it appears
          on the Build checklist. <strong>Request a change</strong> (or the
          conversation on the right) tells the AI what to fix &mdash; it
          rebuilds the model and replies; anything that doesn&rsquo;t change
          keeps your verdict. <strong>Reject</strong> drops it from the
          checklist. None of the three write anything to production pricing.
        </li>
        <li>
          Low-confidence proposals and anything listed above as still needed are
          the places to look first.
        </li>
      </ul>
    </div>
  );
}

/** The hard blockers of `MissingInputsPanel`: documents to provide, and other information needed. */
function HardBlockers({ items }: { items: string[] }) {
  const documents = items.filter(looksLikeMissingDocument);
  const other = items.filter((i) => !looksLikeMissingDocument(i));
  return (
    <>
      <div className="flex align-items-center gap-2 mb-2">
        <i className="pi pi-exclamation-triangle text-orange-500" />
        <span className="font-semibold">
          Modeling is blocked &mdash; {items.length} required input
          {items.length === 1 ? "" : "s"} not found in the documents or in
          ClearHealth
        </span>
      </div>
      <p className="text-700 text-sm mt-0 mb-3">
        These are hard blockers, not caveats &mdash; the affected components
        could not be modeled at all. Gather them, then start a{" "}
        <strong>new {CONTRACT_MODEL}</strong> with the fuller set; this{" "}
        {CONTRACT_MODEL} stays as a record. Softer &ldquo;please confirm&rdquo;
        items appear as <strong>open questions</strong> on the individual
        proposals below.
      </p>

      {documents.length > 0 ? (
        <div className="mb-3">
          <div className="font-medium text-sm mb-1">Documents to provide</div>
          <ul className="mt-0 mb-0 pl-3 text-sm">
            {documents.map((d) => (
              <li key={d} className="mb-1">
                {d}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {other.length > 0 ? (
        <div>
          <div className="font-medium text-sm mb-1">
            Other information needed
          </div>
          <ul className="mt-0 mb-0 pl-3 text-sm">
            {other.map((o) => (
              <li key={o} className="mb-1">
                {o}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

/**
 * What the run still needs, above the wizard. `items` are the hard blockers (`collectMissingInputs`) — a new
 * contract model is the way past them. `effectiveDateTerms` are the terms waiting for an effective date
 * the documents don't state (CLE-2811): she types it on the card, so each is one line saying where, listed first,
 * and on their own they get a plain heading rather than "Modeling is blocked".
 */
function MissingInputsPanel({
  items,
  effectiveDateTerms,
}: {
  items: string[];
  effectiveDateTerms: { id: string; payerName: string }[];
}) {
  const hardBlocked = items.length > 0;
  return (
    <div
      className={`surface-card border-1 border-round p-3 mb-4${
        hardBlocked ? "" : " surface-border"
      }`}
      style={hardBlocked ? { borderColor: "var(--orange-500)" } : undefined}
    >
      {effectiveDateTerms.length > 0 ? (
        <div className={hardBlocked ? "mb-3" : undefined}>
          <div className="flex align-items-center gap-2 mb-2">
            <i className="pi pi-calendar" />
            <span className="font-semibold">
              {termsNeedEffectiveDate(effectiveDateTerms.length)}
            </span>
          </div>
          <ul className="mt-0 mb-0 pl-3 text-sm">
            {effectiveDateTerms.map((t) => (
              <li key={t.id} className="mb-1">
                {enterEffectiveDateOnCard(t.payerName)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {hardBlocked ? <HardBlockers items={items} /> : null}
    </div>
  );
}

/**
 * The implementation to-do list, as a two-column table: the left column is what to build (name,
 * confidence, how-to), the right column is how it compares to what's already in production for this
 * provider × payer (auto-loaded — verdict plus a one-line detail). Nothing here writes to production.
 */
function ImplementationChecklist({
  recs,
  verification,
  loadingLive,
  onRefresh,
  onToggle,
  onViewDetails,
  resolvePayer,
}: {
  recs: IDraftRecommendation[];
  verification: IDraftVerification | null;
  loadingLive: boolean;
  onRefresh: () => void;
  onToggle: (recId: string, implemented: boolean) => void;
  onViewDetails: (recId: string) => void;
  /** CLE-2811 — names each payer group and each build step's payer; never "Payer 87 (87)". */
  resolvePayer: PayerNameResolver;
}) {
  const onChecklist = recs.filter(isOnChecklist);
  const groups = namePayerGroups(groupByPayer(onChecklist), resolvePayer);
  const payerNameFor = (rec: IDraftRecommendation): string | undefined => {
    const { id: pid, name } = recPayer(rec);
    return pid != null ? resolvePayer(pid, name) : undefined;
  };
  const doneCount = onChecklist.filter((r) => r.implementedAt).length;
  const multiPayer = groups.filter((g) => g.payerId != null).length > 1;

  return (
    <div
      id="implementation-checklist"
      className="surface-card border-1 surface-border border-round p-3 mb-4"
    >
      <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <span className="font-semibold text-lg">Implementation checklist</span>
        <div className="flex align-items-center gap-3">
          <span className="text-700 text-sm">
            {doneCount}/{onChecklist.length} marked done
          </span>
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            className="p-button-sm p-button-text"
            loading={loadingLive}
            onClick={onRefresh}
          />
        </div>
      </div>
      <ReviewExplainer count={recs.length} />
      <p className="text-700 text-sm mt-0 mb-3 line-height-3">
        Build each item in the Payer Rules UI, then tick it off. The{" "}
        <strong>In production</strong> column shows what&rsquo;s already in the
        database for this provider &amp; payer so you can confirm each value
        matches (you judge equivalence &mdash; e.g. a precomputed base file vs.
        layered percentages can be the same thing). Hit <strong>Refresh</strong>{" "}
        after you build something to re-check.
        {multiPayer ? (
          <>
            {" "}
            This contract covers{" "}
            <strong>
              {groups.filter((g) => g.payerId != null).length} payers
            </strong>{" "}
            — each row is grouped under the payer it&rsquo;s for.
          </>
        ) : null}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr className="text-left text-700 text-sm">
              <th className="font-medium py-2 pr-4" style={{ width: "58%" }}>
                To build
              </th>
              <th className="font-medium py-2">In production</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <Fragment key={group.key}>
                <tr>
                  <td colSpan={2} className="pt-4 pb-1">
                    <span className="text-xs uppercase font-semibold text-600 tracking-wide flex align-items-center gap-2">
                      <i
                        className={
                          group.payerId != null
                            ? "pi pi-building text-700"
                            : "pi pi-sitemap text-700"
                        }
                        style={{ fontSize: "0.8rem" }}
                      />
                      {group.payerId != null
                        ? group.payerName ?? UNKNOWN_PAYER
                        : "Contract-wide (all payers)"}
                    </span>
                  </td>
                </tr>
                {group.recs.map((rec) => {
                  const subtitle = recSubtitle(rec);
                  return (
                    <tr key={rec.id} className="border-top-1 surface-border">
                      <td className="py-3 pr-4 align-top">
                        <div className="flex gap-3 align-items-start">
                          <Checkbox
                            inputId={`impl-${rec.id}`}
                            checked={!!rec.implementedAt}
                            onChange={(e) => onToggle(rec.id, !!e.checked)}
                            className="mt-1"
                          />
                          <div style={{ minWidth: 0 }}>
                            <div className="flex flex-wrap align-items-center gap-2 mb-1">
                              <span className="font-semibold">
                                {componentTypeLabel(rec.componentType)}
                              </span>
                              <Tag
                                severity={confidenceSeverity(rec.confidence)}
                                value={`${rec.confidence} confidence`}
                              />
                            </div>
                            {subtitle ? (
                              <div className="text-sm text-700 font-medium mb-1">
                                {subtitle}
                              </div>
                            ) : null}
                            {/* Concrete, value-filled steps — what to type, not just what to make.
                                "View details" still jumps to the full editable form below. */}
                            <ol className="text-sm text-700 mt-1 mb-0 pl-3 line-height-3">
                              {buildSteps(rec, {
                                payerName: payerNameFor(rec),
                              }).map((step, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <li key={i} className="mb-1">
                                  {step}
                                </li>
                              ))}
                            </ol>
                            {buildStepsNote(rec) ? (
                              <div className="text-sm text-600 mt-2 p-2 surface-50 border-round line-height-3">
                                <span className="font-medium">Note: </span>
                                {buildStepsNote(rec)}
                              </div>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => onViewDetails(rec.id)}
                              className="inline-flex align-items-center gap-1 text-sm font-medium no-underline p-0 mt-2 bg-transparent border-none cursor-pointer"
                              style={{ color: "var(--primary-color)" }}
                            >
                              <i
                                className="pi pi-arrow-down"
                                style={{ fontSize: "0.75rem" }}
                              />
                              View / edit fields
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 align-top">
                        <ProductionCell
                          rec={rec}
                          match={verification?.[rec.id]}
                          loading={loadingLive && !verification}
                        />
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * CLE-2811 — under the Save draft toast: a link to each contract saved to, why any term failed, and each
 * term it left alone on purpose (overridden, or still needing input).
 */
function SaveDraftToastDetail({ summary }: { summary: ISaveDraftSummary }) {
  return (
    <div className="flex flex-column gap-1 mt-1">
      {summary.links.map((l) => (
        <Link key={l.contractId} to={`/payer-rules/contracts/${l.contractId}`}>
          {l.label}
        </Link>
      ))}
      {summary.failures.map((f) => (
        <span key={f}>{f}</span>
      ))}
      {summary.notSaved.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </div>
  );
}

export function ContractModelDraftDetail({
  ratesStageEnabled = RATES_STAGE_ENABLED,
}: {
  /**
   * CLE-2811 (2026-09-28) — whether the rates stage ("Continue to rates", the locked "Rates & fee
   * schedules" rail row) is offered. `RATES_STAGE_ENABLED` (off) unless a test says otherwise.
   */
  ratesStageEnabled?: boolean;
} = {}) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * CLE-2811 — where "back" goes. A run opened from a contract page carries that page's URL in
   * navigation state; going "back to runs" from there would drop the user into a list they never
   * visited. State is per navigation, so a reload or a shared link falls back to the run list.
   */
  const cameFrom = (location.state as { from?: string } | null)?.from ?? null;
  const toastRef = useContext(ToastContext);

  /**
   * CLE-2811 — while a pass is in flight the page polls the SLIM `GET /runs/:id/progress` every 1.5 s and
   * refetches the whole run (every recommendation, attribution and message) only when the slim response
   * says it moved — status, latest version, message or version count (`shouldRefetchRun`) — plus a 10-s
   * safety net. It used to refetch the whole run every 4 s. When the pass ends, the status change brings
   * the result in (one final full refetch) and the slim poll stops with it. Nothing polls once settled.
   */
  const [processing, setProcessing] = useState(false);
  const { data: run, refetch: refetchRun } = useGetContractModelDraftRunQuery(
    id,
    {
      skip: !id,
      pollingInterval: processing ? RUN_SAFETY_REFETCH_MS : 0,
    }
  );
  useEffect(() => {
    setProcessing(!!run && isProcessing(run.status));
  }, [run]);
  // When this pass started, as far as this page knows — a slim response older than that is the previous
  // pass's (RTK keeps a hook's last data across `skip`), so it is ignored (`freshProgress`).
  const passSinceRef = useRef<number | null>(null);
  if (processing && passSinceRef.current === null)
    passSinceRef.current = Date.now();
  if (!processing && passSinceRef.current !== null) passSinceRef.current = null;
  const { data: progressData, fulfilledTimeStamp: progressFetchedAt } =
    useGetContractModelDraftRunProgressQuery(id, {
      skip: !id || !processing,
      pollingInterval: processing ? PROGRESS_POLL_MS : 0,
      refetchOnMountOrArgChange: true,
    });
  const slim = freshProgress(
    progressData,
    progressFetchedAt,
    passSinceRef.current
  );
  // The last slim response's change key this pass; null between passes, so a pass starts from the run.
  const lastChangeRef = useRef<IRunChangeKey | null>(null);
  useEffect(() => {
    if (!processing) lastChangeRef.current = null;
  }, [processing]);
  useEffect(() => {
    if (!run || !slim || slim.id !== run.id) return;
    const next = progressChangeKey(slim);
    const prev = lastChangeRef.current ?? runChangeKey(run);
    lastChangeRef.current = next;
    if (shouldRefetchRun(prev, next)) refetchRun();
  }, [slim, run, refetchRun]);
  /**
   * CLE-2811 (2026-09-28) — Ani: "Instead of id show the name." The server names every payer id the run
   * refers to (`payerNames`); the slim poll names the ones a pass in flight has just found. Everything the
   * page shows about a payer resolves through these first (`payerNames.ts`).
   */
  const runPayerNames = useMemo(
    () => mergePayerNames(run?.payerNames, slim?.payerNames),
    [run?.payerNames, slim?.payerNames]
  );
  // The run as the progress strip and the conversation's live reply see it: the slim poll's status,
  // clocks, live progress and queue position over the full run, while both say a pass is in flight — and
  // (CLE-2811) the AI's replies, narration and activity with "payer 87" replaced by the payer's name.
  const liveRun = useMemo(
    () =>
      run
        ? humanizeRunPayerRefs(liveRunOf(run, slim), runPayerNames)
        : undefined,
    [run, slim, runPayerNames]
  );

  const [presignDoc] = usePresignContractModelDraftDocumentMutation();
  const [confirmDoc] = useConfirmContractModelDraftDocumentMutation();
  const [runGeneration, { isLoading: generating }] =
    useRunContractModelDraftGenerationMutation();
  const [retrying, setRetrying] = useState(false);
  // The reviewer's steering for this run, seeded from what was stored and editable before generating.
  const [instructions, setInstructions] = useState("");
  const seededRunIdRef = useRef<string | null>(null);
  // Seed the box from the stored value ONCE per run — but never clobber what the reviewer is typing.
  // Polling refreshes `run` every few seconds; re-seeding on every poll would erase mid-edit keystrokes.
  useEffect(() => {
    if (run && seededRunIdRef.current !== run.id) {
      seededRunIdRef.current = run.id;
      setInstructions(run.generationInstructions ?? "");
    }
  }, [run]);
  const [accept, { isLoading: accepting }] =
    useAcceptContractModelDraftRecommendationMutation();
  const [reject, { isLoading: rejecting }] =
    useRejectContractModelDraftRecommendationMutation();
  const [restore, { isLoading: restoring }] =
    useRestoreContractModelDraftRecommendationMutation();
  const [chat] = useChatContractModelDraftRunMutation();
  const [deleteMessage] = useDeleteContractModelDraftMessageMutation();
  const [regenerate, { isLoading: regenerating }] =
    useRegenerateContractModelDraftRunMutation();
  const [continueToRates, { isLoading: continuing }] =
    useContinueContractModelDraftRunMutation();
  const [cancelRun, { isLoading: canceling }] =
    useCancelContractModelDraftRunMutation();
  const [stopPass, { isLoading: stoppingPass }] =
    useStopContractModelDraftPassMutation();
  const [deleteRun, { isLoading: deleting }] =
    useDeleteContractModelDraftRunMutation();
  const [setImplemented] =
    useSetContractModelDraftRecommendationImplementedMutation();
  const busy = accepting || rejecting || restoring;

  // Production comparison, auto-loaded (no toggle) once the run has recommendations, and re-pullable
  // via Refresh after the user builds something. Keyed by recommendationId; null until first loaded.
  const {
    data: verification = null,
    isFetching: loadingLive,
    refetch: refetchVerification,
  } = useGetContractModelDraftVerificationQuery(id, {
    skip: !id || !run?.recommendations?.length,
  });

  // "This contract looks modeled under a different provider" — only returns siblings when the run's own
  // provider+payer has nothing, so a non-empty list is a strong wrong-provider signal.
  const { data: providerHint } = useGetContractModelDraftProviderHintQuery(id, {
    skip: !id || !run?.recommendations?.length,
  });

  // Base rate files already loaded for this provider+payer — shown on base_rate_file cards as a
  // possible reuse. One run-level fetch, shared by every card via RTK cache.
  const { data: existingBrf } =
    useGetContractModelDraftExistingBaseRateFilesQuery(id, {
      skip: !id || !run?.recommendations?.length,
    });
  const existingBaseRateFiles = existingBrf?.files ?? [];

  // Payer/provider name lookups so proposedModeling shows "Aetna Medicare Advantage", not a bare id.
  const { data: payersData } = useGetAllPayersQuery();
  const payerOptions: IPayerOption[] = useMemo(
    () =>
      (
        (payersData?.data ?? []) as {
          id: number;
          name: string;
          type?: string;
        }[]
      )
        .map((p) => ({ id: p.id, name: p.name, type: p.type ?? null }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [payersData]
  );

  // The base rate files loaded for this account, so "File to take a % of" is a pick-list of real files
  // instead of free text — the source of the "two files named X" ambiguity the reviewer hit.
  const { data: baseRateFilesData } = useGetAllBaseRateFilesQuery(
    { accountId: run?.accountId },
    { skip: !run?.accountId }
  );
  const baseRateFileNames: string[] = useMemo(() => {
    const names = (baseRateFilesData?.data ?? [])
      .map((f) => f.displayName)
      .filter((n): n is string => typeof n === "string" && n.trim() !== "");
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [baseRateFilesData]);
  // {id, displayName} per file, deduped to the newest version of each name — so a reuse_existing pick
  // writes a valid id alongside the name (resolution downstream still lands on the latest version).
  const baseRateFileOptions = useMemo(() => {
    const byName = new Map<
      string,
      { id: string; displayName: string; version: number }
    >();
    (baseRateFilesData?.data ?? []).forEach((f) => {
      if (typeof f.displayName !== "string" || f.displayName.trim() === "")
        return;
      const prev = byName.get(f.displayName);
      if (!prev || (f.version ?? 0) > prev.version) {
        byName.set(f.displayName, {
          id: f.id,
          displayName: f.displayName,
          version: f.version ?? 0,
        });
      }
    });
    return Array.from(byName.values()).map((f) => ({
      id: f.id,
      displayName: f.displayName,
    }));
  }, [baseRateFilesData]);
  const { data: providersData } = useGetAllProvidersQuery();
  /**
   * CLE-2811 (2026-09-28) — one name for every payer and provider id on the page: the run's `payerNames`,
   * then the payers list, then the agent's own name, then "Unknown payer" — never the id (`payerNames.ts`).
   */
  const resolvePayer = useMemo(
    () =>
      payerNameResolver({
        runNames: runPayerNames,
        payers: (payersData?.data ?? []) as { id: number; name: string }[],
      }),
    [runPayerNames, payersData]
  );
  const resolveProvider = useMemo(
    () =>
      providerNameResolver({
        runProvider: run
          ? { id: run.providerId, name: run.providerName }
          : null,
        providers: providersData?.data ?? [],
      }),
    [run, providersData]
  );
  const resolveIdName = useMemo<IdResolver>(
    () => (key, value, fallbackName) => {
      if (value === null || value === undefined || value === "") return null;
      if (isPayerKey(key)) return resolvePayer(value, fallbackName);
      if (isProviderKey(key)) return resolveProvider(value, fallbackName);
      return null;
    },
    [resolvePayer, resolveProvider]
  );
  /** A recommendation's payer as she reads it; a term that names none is the run's own payer's. */
  const payerLabelOf = useCallback(
    (rec: IDraftRecommendation): string => {
      const { id: payerId, name } = recPayer(rec);
      if (payerId != null) return resolvePayer(payerId, name);
      return name ?? run?.payerName ?? UNKNOWN_PAYER;
    },
    [resolvePayer, run?.payerName]
  );

  // Which recommendation card to briefly ring after a "View details" jump, so the eye lands on it.
  const [focusRecId, setFocusRecId] = useState<string | null>(null);
  const scrollToId = (elementId: string) =>
    document
      .getElementById(elementId)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  const [fetchBaseRateCsv] = useLazyGetContractModelDraftBaseRateFileCsvQuery();
  const [generatingCsvId, setGeneratingCsvId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Name of the file currently uploading, or null. Doubles as the progress indicator's label.
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const uploading = uploadingFile !== null;
  const [overrideTarget, setOverrideTarget] =
    useState<IDraftRecommendation | null>(null);
  const [overrideReason, setOverrideReason] = useState("");

  const allRecs = useMemo(() => run?.recommendations ?? [], [run]);

  /**
   * CLE-2811 — the accepted contract term, which is what can be written to a contract. Only one
   * exists per run; `accepted` is the gate because writing a recommendation nobody has reviewed to a
   * production contract is exactly the failure this tool is built to avoid.
   */
  // CLE-2811 — EVERY accepted contract term gets a save panel. One document often prices several payers
  // (Medflex, SuperMed, ACA), one term each; only the first accepted one used to be saveable at all.
  const acceptedContractTerms = useMemo(
    () =>
      allRecs
        .filter(
          (r) => r.componentType === "contract_term" && r.status === "accepted"
        )
        .map((r) => ({
          id: r.id,
          payerName: payerLabelOf(r),
          proposedModeling: r.proposedModeling,
        })),
    [allRecs, payerLabelOf]
  );

  // ---- Save draft (CLE-2811, 2026-09-28) ------------------------------------------------------------
  // Ani: "It should be Save Draft." The facts run's last button accepts every contract term she has not
  // rejected, overridden or left needing input (with the edits on its card) and saves each onto its
  // contract, fill-only (`saveDraft.ts`).
  const [applyTerm] = useApplyContractModelDraftContractTermMutation();
  /** Each card's unsaved edits, by recommendation id — kept in a ref: typing must not re-render the page. */
  const draftEditsRef = useRef<Record<string, Record<string, unknown>>>({});
  const handleDraftChange = useCallback(
    (recId: string, edits: Record<string, unknown> | null) => {
      if (edits) draftEditsRef.current[recId] = edits;
      else delete draftEditsRef.current[recId];
    },
    []
  );
  const [savingDraft, setSavingDraft] = useState(false);
  /** Terms Save draft skipped because their payer has several contracts: highlighted until the next one. */
  const [needsChoiceIds, setNeedsChoiceIds] = useState<string[]>([]);
  // A term blocked only by the effective date counts: typing the date on its card (her edits are in a ref, not
  // state) makes it one Save draft saves. A hard-blocked term does not.
  const saveableTermCount = useMemo(
    () => saveDraftCandidateCount(allRecs),
    [allRecs]
  );
  const attentionFor = (rec: IDraftRecommendation): string | null =>
    needsChoiceIds.includes(rec.id)
      ? pickContractAttention(payerLabelOf(rec))
      : null;

  // ---- Wizard -------------------------------------------------------------------------------------
  const [activeStep, setActiveStep] = useState<StepId>("terms");
  const activeStepMeta =
    WIZARD_STEPS.find((s) => s.id === activeStep) ?? WIZARD_STEPS[1];
  const stepIndex = WIZARD_STEPS.findIndex((s) => s.id === activeStep);
  const stepRecs = useMemo(
    () => recommendationsForStep(allRecs, activeStep),
    [allRecs, activeStep]
  );
  // Pass ALL recs as context so a base rate file (which carries no payer of its own) can be grouped under
  // the payer of the rate calc that references it — otherwise two payers' files pile into "Contract-wide".
  const stepPayerGroups = useMemo(
    () => namePayerGroups(groupByPayer(stepRecs, allRecs), resolvePayer),
    [stepRecs, allRecs, resolvePayer]
  );

  /**
   * How far the reviewer has unlocked. Held as a high-water mark that only moves forward.
   *
   * A regeneration resets modified recommendations to `pending`, which can make an *already-passed*
   * step start blocking again. Recomputing the gate from scratch would then relegate the reviewer to
   * an earlier step mid-review and lose their place, for a change they haven't even read yet. The
   * Changes screen is what surfaces that a regeneration moved something; the lock stays put.
   */
  const [reachedIndex, setReachedIndex] = useState(0);
  const unlockedThrough = useMemo(
    () => unlockedThroughIndex(allRecs, reachedIndex),
    [allRecs, reachedIndex]
  );
  const currentStepBlocks = stepBlocksAdvance(allRecs, activeStep);

  // Passing a step is what advances the mark; it never retreats on its own.
  useEffect(() => {
    if (stepIndex > reachedIndex) setReachedIndex(stepIndex);
  }, [stepIndex, reachedIndex]);
  /**
   * The two steps that hold a rate sheet render as a grid instead of cards. Grouping there is by
   * SERVICE LINE (the grid's own concern), not by payer — a run is one payer anyway, so the payer
   * accordion buys nothing on these steps and costs the at-a-glance view the grid exists to give.
   */
  const isGridStep = activeStep === "inpatient" || activeStep === "outpatient";
  /** Adjustment Rules reads as sentences with switches (v0 step 5), not typed cards. */
  const isRulesStep = activeStep === "adjustment_rules";

  /**
   * Jump to a recommendation from the checklist or the Changes screen.
   *
   * Since the wizard split the page into steps, the recommendation is almost never ON the step you are
   * looking at — so the old "scroll to it" did nothing at all. Switch to its step first, then scroll.
   * (The timeout lets the step render before we look for the anchor.)
   */
  const handleViewDetails = (recId: string) => {
    const rec = allRecs.find((r) => r.id === recId);
    if (rec) setActiveStep(stepForRecommendation(rec));
    setFocusRecId(recId);
    window.setTimeout(() => scrollToId(`rec-${recId}`), 120);
    window.setTimeout(
      () => setFocusRecId((cur) => (cur === recId ? null : cur)),
      2400
    );
  };
  const unresolvedHere = unresolvedInStep(allRecs, activeStep);

  // ---- Service-line picker ------------------------------------------------------------------------
  // Seeded from the run: a line is "selected" unless every recommendation in it is already rejected —
  // so the picker reflects reality on reload rather than resetting to all-selected.
  const availableLines = useMemo(() => serviceLinesIn(allRecs), [allRecs]);
  // The payer segments (lines of business) this contract covers — contract-wide recs carry no payer and
  // are not a segment you can decline, so they're excluded (same as no-service-line recs in the SL picker).
  const segments = useMemo(
    () =>
      namePayerGroups(
        groupByPayer(allRecs).filter((g) => g.payerId != null),
        resolvePayer
      ),
    [allRecs, resolvePayer]
  );

  // Steps hidden from the rail must also be skipped by Back / Confirm & continue, or the linear nav
  // would land on an empty page. ONE list drives both (`visibleSteps`): the run's stage (a facts run
  // shows only the facts steps), its contract type (no Inpatient for ASC/Professional), "What Changed"
  // until there's a second version, Lines of Business unless the contract spans more than one, and
  // Service Lines when the contract has none. The upload screen is never a wizard page here.
  const hasInpatientRecommendations = useMemo(
    () => recommendationsForStep(allRecs, "inpatient").length > 0,
    [allRecs]
  );
  // CLE-2811 — while a pass runs the wizard stays on screen and fills in as the worker reads. The worker's
  // `partial` (plans, each plan's terms, the contract facts) previews on Lines of Business and Contract
  // Terms until the real recommendations arrive; kept through worker_returned, when the worker has
  // already cleared its progress.
  const liveProgress = useLiveProgress({
    status: liveRun?.status ?? "collecting",
    versionNo: liveRun?.versionNo ?? 0,
    progress: liveRun?.progress ?? null,
  });
  const preview = useMemo(
    () =>
      partialPreviewOf(liveProgress, (payerId, workerName) =>
        resolvePayer(payerId, workerName)
      ),
    [liveProgress, resolvePayer]
  );
  const passRunning = !!run && isProcessing(run.status);
  /** A first generation: nothing real to show yet, so every step previews or shows skeletons. */
  const firstPass = passRunning && allRecs.length === 0;
  /**
   * CLE-2811 — facts first: the first pass has saved the contract facts (`interimAt`) and is still reading
   * the schedules. Contract Terms (and Lines of Business) show the real recommendations; their empty
   * reimbursement slots say "Extracting the schedule…" until the pass completes and fills them.
   */
  const interim = !!run && isInterimRun(run);
  const visible = useMemo(
    () =>
      visibleSteps(
        run?.stage ?? "full",
        run?.contractType ?? null,
        segments.length > 1 ||
          (firstPass && showSegmentsWhileGenerating(preview)),
        {
          // CLE-2811 (in place) — also at version 1, once a chat change landed on a card in place.
          hasChanges: !!run && runHasChanges(run),
          hasServiceLines: availableLines.length > 0,
          hasInpatientRecommendations,
        }
      ),
    [
      run?.stage,
      run?.contractType,
      run,
      segments.length,
      availableLines.length,
      hasInpatientRecommendations,
      firstPass,
      preview,
    ]
  );
  // CLE-2811 (2026-09-28) — the rates stage's entry points, hidden by `ratesStageEnabled` (`featureFlags.ts`).
  const rateEntries = ratesStageEntries(
    run?.stage ?? "full",
    ratesStageEnabled
  );
  const building = stepsStillBuilding(
    visible,
    allRecs.length,
    passRunning,
    interim
  );
  /**
   * The active step still previews: every step on a first pass; in the facts-first window only the steps
   * still being built (a full pass's rate steps) — Contract Terms has the saved facts.
   */
  const stepPreviewing =
    firstPass || (interim && building.includes(activeStep));
  const stepHidden = useCallback(
    (s: StepId): boolean => s === "documents" || !visible.includes(s),
    [visible]
  );
  /** The next visible step in `dir` (+1 forward, -1 back), or null if there is none. */
  const adjacentStep = (dir: 1 | -1): StepId | null => {
    for (let i = stepIndex + dir; i >= 0 && i < WIZARD_STEPS.length; i += dir) {
      if (!stepHidden(WIZARD_STEPS[i].id)) return WIZARD_STEPS[i].id;
    }
    return null;
  };
  const prevStep = adjacentStep(-1);
  const nextStep = adjacentStep(1);

  // Segment first (Ani's principle): when the run first loads and the contract spans more than one line
  // of business, open the Lines of Business step, so LOB scope is settled before diving into a payer's
  // terms. One-shot per run (keyed by id) so it never yanks the reviewer back after they've navigated.
  const autoLandedRunRef = useRef<string | null>(null);
  useEffect(() => {
    if (!run?.id) return;
    // CLE-2811 — she watched this pass fill the wizard in: when its recommendations arrive they take the
    // previews' place where she is. Moving her to another step then would be the jump the page avoids.
    if (!run.recommendations?.length && isProcessing(run.status)) {
      autoLandedRunRef.current = run.id;
      return;
    }
    if (!run.recommendations?.length) return;
    if (autoLandedRunRef.current === run.id) return;
    // Only CONSUME the one-shot when we actually land. If the step isn't visible yet — a poll caught the
    // recs mid-persist, so a second payer's rec hasn't arrived and segments is momentarily 1 — leave the
    // ref unset so we still land once it does. Consuming it here would block the landing forever.
    if (stepHidden("segments")) return;
    autoLandedRunRef.current = run.id;
    setActiveStep("segments");
  }, [run?.id, run?.status, run?.recommendations?.length, stepHidden]);

  // CLE-2811 — Lines of Business can show during a first pass and go when it finds a single plan; don't
  // leave her on a step the rail no longer lists.
  useEffect(() => {
    if (activeStep === "segments" && stepHidden("segments"))
      setActiveStep("terms");
  }, [activeStep, stepHidden]);

  const [selectedLines, setSelectedLines] = useState<string[] | null>(null);
  useEffect(() => {
    if (selectedLines !== null || availableLines.length === 0) return;
    setSelectedLines(
      availableLines.filter((line) =>
        allRecs.some(
          (r) => serviceLineOf(r) === line && r.status !== "rejected"
        )
      )
    );
  }, [availableLines, allRecs, selectedLines]);

  const handleToggleServiceLine = (line: string) =>
    setSelectedLines((prev) => {
      const current = prev ?? availableLines;
      return current.includes(line)
        ? current.filter((l) => l !== line)
        : [...current, line];
    });

  /**
   * Apply the picker: reject everything in a skipped line, restore everything in a kept one.
   * Rejected recs already drop off the implementation checklist, so a skipped line simply means nobody
   * is ever told to build it. Nothing is deleted, and re-ticking the line puts it back.
   */
  const handleApplyServiceLines = async () => {
    const keep = selectedLines ?? availableLines;
    // Recs with no service line are contract-wide — not a line you can decline, so never touched.
    const inAPickedLine = allRecs.filter((r) => serviceLineOf(r) !== null);
    const toReject = inAPickedLine.filter(
      (r) =>
        !keep.includes(serviceLineOf(r) as string) && r.status !== "rejected"
    );
    const toRestore = inAPickedLine.filter(
      (r) =>
        keep.includes(serviceLineOf(r) as string) && r.status === "rejected"
    );

    try {
      await Promise.all([
        ...toReject.map((r) => reject(r.id).unwrap()),
        ...toRestore.map((r) => restore(r.id).unwrap()),
      ]);
      const skipped = availableLines.filter((l) => !keep.includes(l));
      toastRef?.current?.show({
        severity: "success",
        summary: "Service lines applied",
        detail: skipped.length
          ? `Skipping ${skipped.join(", ")}.`
          : "Modeling every service line.",
        life: 5000,
      });
      setActiveStep("inpatient");
    } catch {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't apply the service-line selection",
        life: 5000,
      });
    }
  };

  // ---- Lines of business (payer segments) — same mechanism as the service-line picker, by payer -------
  const [selectedPayers, setSelectedPayers] = useState<string[] | null>(null);
  useEffect(() => {
    if (selectedPayers !== null || segments.length === 0) return;
    // Default: a segment starts ticked if any of its recs is still live (a fully-rejected one starts off).
    setSelectedPayers(
      segments
        .filter((g) => g.recs.some((r) => r.status !== "rejected"))
        .map((g) => g.key)
    );
  }, [segments, selectedPayers]);

  const handleTogglePayer = (key: string) =>
    setSelectedPayers((prev) => {
      const current = prev ?? segments.map((g) => g.key);
      return current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key];
    });

  /**
   * Apply the picker: reject every recommendation under a skipped line of business (its contract term,
   * base rate file, rate calc, rules), restore them for a kept one. A rejected rec drops off the
   * checklist, so a skipped line of business is simply never built; re-ticking puts it back. This is the
   * "reject the whole payer" cascade — a contract term never leaves its base rate file / rate calc behind.
   */
  const handleApplySegments = async () => {
    const keep = selectedPayers ?? segments.map((g) => g.key);
    const recKey = new Map<string, string>();
    segments.forEach((g) => g.recs.forEach((r) => recKey.set(r.id, g.key)));
    const toReject = allRecs.filter((r) => {
      const k = recKey.get(r.id);
      return k != null && !keep.includes(k) && r.status !== "rejected";
    });
    const toRestore = allRecs.filter((r) => {
      const k = recKey.get(r.id);
      return k != null && keep.includes(k) && r.status === "rejected";
    });

    try {
      await Promise.all([
        ...toReject.map((r) => reject(r.id).unwrap()),
        ...toRestore.map((r) => restore(r.id).unwrap()),
      ]);
      const skipped = segments.filter((g) => !keep.includes(g.key));
      toastRef?.current?.show({
        severity: "success",
        summary: "Lines of business applied",
        detail: skipped.length
          ? `Skipping ${skipped
              .map((g) => g.payerName ?? UNKNOWN_PAYER)
              .join(", ")}.`
          : "Modeling every line of business.",
        life: 5000,
      });
      setActiveStep("terms");
    } catch {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't apply the line-of-business selection",
        life: 5000,
      });
    }
  };

  /**
   * CLE-2811 — the conversation. The server regenerates straight away, or queues her message behind the
   * pass in flight (it fires on its own when that pass finishes). Null on failure, so the panel keeps
   * her text.
   */
  const handleChat = async (
    body: string,
    stepId: StepId,
    recommendationId?: string
  ): Promise<{ queuedOnly: boolean } | null> => {
    if (!id) return null;
    try {
      const { queuedOnly } = await chat({
        runId: id,
        body,
        stepId,
        recommendationId,
      }).unwrap();
      return { queuedOnly };
    } catch (e) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't send that to the AI",
        detail: errorDetail(e),
        life: 7000,
      });
      return null;
    }
  };

  const handleDeleteInstruction = async (messageId: string) => {
    if (!id) return;
    try {
      await deleteMessage({ runId: id, messageId }).unwrap();
    } catch (e) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't remove it",
        detail: errorDetail(e),
        life: 5000,
      });
    }
  };

  const handleRegenerate = async () => {
    if (!id) return;
    try {
      await regenerate(id).unwrap();
      toastRef?.current?.show({
        severity: "success",
        summary: "Re-deriving the model",
        // Say plainly what will happen, including that her work survives — that is the whole reason
        // it is safe to keep working while this runs.
        detail:
          "This takes a few minutes. Keep reviewing — anything that doesn't change keeps your verdict.",
        life: 7000,
      });
    } catch (e) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't regenerate",
        detail: errorDetail(e),
        life: 7000,
      });
    }
  };

  /**
   * CLE-2811 — Retry on a failed or dead pass (the conversation's error bubble). `regenerate` when the
   * run's latest version has a model to diff against; otherwise `generate`, since the server refuses to
   * regenerate an empty version (409). Generate re-sends the stored instructions: its body is
   * authoritative, and an absent field would clear them.
   */
  const handleRetry = async () => {
    if (!id || !run || retrying) return;
    setRetrying(true);
    try {
      const started =
        retryModeOf(run) === "regenerate"
          ? await regenerate(id).unwrap()
          : await runGeneration({
              runId: id,
              instructions: run.generationInstructions ?? "",
            }).unwrap();
      // Stays "retrying" until the refetched run has left failed/dead (the effect below), so the error
      // bubble does not flash back between this response and the refetch its invalidation triggers.
      if (isFailedRun(started.status)) setRetrying(false);
      toastRef?.current?.show({
        severity: "success",
        summary: RETRY_STARTED,
        life: 4000,
      });
    } catch (e) {
      setRetrying(false);
      toastRef?.current?.show({
        severity: "error",
        summary: RETRY_FAILED,
        detail: errorDetail(e),
        life: 7000,
      });
    }
  };
  useEffect(() => {
    if (retrying && run && !isFailedRun(run.status)) setRetrying(false);
  }, [retrying, run]);

  /**
   * CLE-2811 — "Continue to rates": the facts are settled, now model everything else. The server flips
   * the run to the full stage and regenerates; the accepted contract term keeps its verdict when the
   * full pass re-derives it unchanged.
   */
  const handleContinueToRates = async () => {
    if (!id) return;
    try {
      await continueToRates(id).unwrap();
      toastRef?.current?.show({
        severity: "success",
        summary: "Modeling rates",
        detail:
          "Rates, carve-outs and rules are being modeled from this document. This takes several minutes.",
        life: 7000,
      });
    } catch (e) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't continue to rates",
        detail: errorDetail(e),
        life: 7000,
      });
    }
  };

  /**
   * Cancel the run. It stays readable, but it no longer counts as the contract's active run, so "Model
   * with AI" starts a fresh one instead of reopening this.
   */
  const handleCancelRun = () => {
    if (!id) return;
    confirmDialog({
      header: CANCEL_MODEL_HEADER,
      message: CANCEL_MODEL_MESSAGE,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: CANCEL_MODEL,
      rejectLabel: "Keep it",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await cancelRun(id).unwrap();
          toastRef?.current?.show({
            severity: "success",
            summary: MODEL_CANCELED,
            life: 4000,
          });
        } catch (e) {
          toastRef?.current?.show({
            severity: "error",
            summary: CANCEL_MODEL_FAILED,
            detail: errorDetail(e),
            life: 7000,
          });
        }
      },
    });
  };

  /**
   * CLE-2811 — Stop: every Stop on the page (the progress strip's, the chat's live reply's) stops the PASS in flight, at
   * once — no dialog. The run stays: back on the model she had, back to Generate on a first pass that saved nothing,
   * or on the facts a first pass already saved. The server's refusal (the result is being saved; nothing is running)
   * is the toast. "Cancel model" (the header) is the only way to end the run.
   */
  const handleStopPass = async () => {
    if (!id || stoppingPass) return;
    try {
      await stopPass(id).unwrap();
    } catch (e) {
      toastRef?.current?.show({
        severity: "error",
        summary: STOP_FAILED,
        detail: errorDetail(e),
        life: 7000,
      });
    }
  };

  /**
   * Delete a canceled run that produced nothing. Offered only when the run is canceled and shows zero
   * recommendations; the server is the authority and its 409 says which condition failed.
   */
  const handleDeleteRun = () => {
    if (!id) return;
    confirmDialog({
      header: DELETE_MODEL_HEADER,
      message: DELETE_MODEL_MESSAGE,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: DELETE_MODEL,
      rejectLabel: "Keep it",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteRun(id).unwrap();
          toastRef?.current?.show({
            severity: "success",
            summary: MODEL_DELETED,
            life: 4000,
          });
          navigate("/contract-model-drafts");
        } catch (e) {
          toastRef?.current?.show({
            severity: "error",
            summary: DELETE_MODEL_FAILED,
            detail: errorDetail(e),
            life: 7000,
          });
        }
      },
    });
  };

  /** Confirm a recommendation, saving the reviewer's inline corrections when they made any. */
  const handleAccept = async (
    recommendationId: string,
    editedModeling?: Record<string, unknown>
  ) => {
    try {
      await accept({ recommendationId, editedModeling }).unwrap();
      // No success toast: the button itself flips to a filled green "Confirmed", which is feedback
      // enough — and the toast landed on top of the "Confirm & continue" button, blocking it.
    } catch (e) {
      // The server re-validates every edit against the same schema the agent's output is held to, so
      // this is where a bad correction (85 instead of 0.85) is caught. Surface its message verbatim.
      const message =
        (e as { data?: { message?: string } })?.data?.message ??
        "Couldn't save this recommendation.";
      toastRef?.current?.show({
        severity: "error",
        summary: "Not saved",
        detail: message,
        life: 8000,
      });
    }
  };

  const handleReject = async (recommendationId: string) => {
    // No success toast, same as confirm: the button flips to a filled "Rejected", and a toast here
    // covered the action buttons the reviewer is working through.
    await reject(recommendationId)
      .unwrap()
      .catch(() => undefined);
  };

  /**
   * CLE-2811 (2026-09-28) — "Save draft": every contract term she has not rejected is confirmed (with the
   * edits on its card) and saved onto its contract, fill-only — the run's contract, the contract it was
   * already saved into, the one existing contract for its payer, or a new draft contract. A term whose
   * payer has several contracts is skipped and highlighted; its save panel is where she picks. A term she
   * overrode, or one still needing input, is never touched — the toast names it. One toast says what
   * happened, with links.
   */
  const handleSaveDraft = async () => {
    if (!id || savingDraft) return;
    const edits = { ...draftEditsRef.current };
    const terms = saveDraftTerms(allRecs, edits, payerLabelOf);
    const skipped = saveDraftSkips(allRecs, edits, payerLabelOf);
    setSavingDraft(true);
    setNeedsChoiceIds([]);
    try {
      const outcomes = await runSaveDraft(terms, {
        runId: id,
        accept: (request) => accept(request).unwrap(),
        apply: (request) => applyTerm(request).unwrap(),
      });
      const result = summarizeSaveDraft([...outcomes, ...skipped]);
      setNeedsChoiceIds(result.needsChoiceIds);
      toastRef?.current?.show({
        severity: result.severity,
        summary: result.summary,
        detail:
          result.links.length > 0 ||
          result.failures.length > 0 ||
          result.notSaved.length > 0 ? (
            <SaveDraftToastDetail summary={result} />
          ) : undefined,
        life: result.severity === "success" ? 8000 : 15000,
      });
      if (result.needsChoiceIds.length > 0) {
        const first = result.needsChoiceIds[0];
        window.setTimeout(() => scrollToId(`apply-term-${first}`), 120);
      }
    } catch (e) {
      toastRef?.current?.show({
        severity: "error",
        summary: SAVE_DRAFT_FAILED,
        detail: errorDetail(e),
        life: 8000,
      });
    } finally {
      setSavingDraft(false);
    }
  };

  // Everything the model said it still needs, rolled up from the individual recommendations — and, apart,
  // the terms waiting only for her to type an effective date the documents don't state.
  const missingInputs = useMemo(
    () => collectMissingInputs(run?.recommendations ?? []),
    [run]
  );
  const effectiveDateTerms = useMemo(
    () =>
      effectiveDateBlockedTerms(run?.recommendations ?? []).map((r) => ({
        id: r.id,
        payerName: payerLabelOf(r),
      })),
    [run, payerLabelOf]
  );

  // Once a run has produced recommendations it is a finished record: uploading more documents or
  // re-generating would append a second set of recommendations to it. Start a new run instead.
  const finished = isRunFinished(run?.recommendations ?? []);

  const hint = run
    ? generateHint(run.documents.length, run.status, { uploading, finished })
    : null;
  // CLE-2811 — the conversation (with the wizard beside it) and the pre-generation Generate row. A contract
  // run with attachments arrives `queued` (the server starts it), so it goes straight to the generating
  // layout; a failed or dead pass keeps the conversation, which is where its reason and Retry are.
  const conversationShown = !!run && showsConversation(run);
  const generateRowShown = !!run && showsGenerateRow(run);

  const toast = (
    severity: "success" | "error",
    summary: string,
    detail: string
  ) => toastRef?.current?.show({ severity, summary, detail, life: 5000 });

  // Generate + download the Medicare CSV a base_rate_file recommendation is built from.
  const handleGenerateCsv = async (
    recId: string,
    proposedModeling?: Record<string, unknown>
  ) => {
    setGeneratingCsvId(recId);
    try {
      // `proposedModeling` is the card's unsaved draft — preview it without committing. Omitted → the
      // persisted recommendation.
      const { blob, fileName } = await fetchBaseRateCsv({
        recommendationId: recId,
        proposedModeling,
      }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast("error", "Couldn't generate the base rate CSV", errorDetail(e));
    } finally {
      setGeneratingCsvId(null);
    }
  };

  const handleToggleImplemented = async (
    recId: string,
    implemented: boolean
  ) => {
    // The checkbox flips instantly via the mutation's optimistic cache update; if the save fails it
    // rolls back and we say so.
    try {
      await setImplemented({
        runId: id,
        recommendationId: recId,
        implemented,
      }).unwrap();
    } catch (e) {
      toast("error", "Couldn't save that", errorDetail(e));
    }
  };

  // presign → PUT (with its deadline) → confirm, shared with the "New contract model" dialog.
  const handleUpload = async (file: File) => {
    if (!isAllowedUpload(file.name)) {
      toast("error", "Unsupported file type", unsupportedFileDetail(file.name));
      return;
    }
    try {
      setUploadingFile(file.name);
      await uploadRunDocument(id, file, {
        presign: presignDoc,
        confirm: confirmDoc,
      });
      toast("success", "Document uploaded", file.name);
    } catch (e) {
      toast("error", `Couldn't upload ${file.name}`, uploadErrorDetail(e));
    } finally {
      setUploadingFile(null);
    }
  };

  /**
   * A recommendation-scoped message. It goes into the SAME conversation as the panel, so there is one
   * mechanism for "tell the agent something", not two that behave differently.
   *
   * The eval signal we used to get from the override form is not lost: an inline edit already records
   * "the agent said X, the human corrected it to Y" (`originalModeling`) with no form to fill in, and
   * this instruction text is itself the record of what the agent got wrong.
   */
  const submitOverride = async () => {
    if (!overrideTarget || !id) return;
    const result = await handleChat(
      overrideReason.trim(),
      activeStep,
      overrideTarget.id
    );
    if (!result) return;
    setOverrideTarget(null);
    setOverrideReason("");
    toastRef?.current?.show({
      severity: "success",
      summary: result.queuedOnly ? "Queued" : "Sent to the AI",
      detail: result.queuedOnly
        ? QUEUED_TOAST_DETAIL
        : "It is rebuilding the model. Anything that doesn't change keeps your verdict.",
      life: 5000,
    });
  };

  return (
    <div className="p-4">
      <div className="flex align-items-center justify-content-between mb-2">
        <Button
          label={cameFrom ? BACK_TO_CONTRACT : BACK_TO_CONTRACT_MODELS}
          icon="pi pi-arrow-left"
          className="p-button-text p-button-sm pl-0"
          onClick={() => navigate(cameFrom ?? "/contract-model-drafts")}
        />
        {/*
          Hidden once the worker has deposited its output (`worker_returned`) and after a terminal status —
          the server refuses a cancel then (409). A running run can be canceled here (CLE-2811) — the ONLY way to end
          the run: every Stop on the page stops just the pass in flight (`handleStopPass`), with no dialog.
        */}
        {run && canCancelRun(run.status) ? (
          <Button
            label={CANCEL_MODEL}
            icon="pi pi-times"
            className="p-button-text p-button-sm p-button-danger"
            loading={canceling}
            onClick={handleCancelRun}
          />
        ) : null}
        {run && canDeleteRun(run) ? (
          <Button
            label={DELETE_MODEL}
            icon="pi pi-trash"
            className="p-button-text p-button-sm p-button-danger"
            loading={deleting}
            onClick={handleDeleteRun}
          />
        ) : null}
      </div>

      {!run ? (
        <p className="text-700">{LOADING_CONTRACT_MODEL}</p>
      ) : (
        <>
          {/*
            The heading is the SUBJECT of the run — which provider's contract with which payer — not
            "Run d7ecc1cf". A truncated uuid told the reviewer nothing about what they had opened, and
            the sentence underneath then repeated the provider and payer a third time, after the tags.
            The id is still here because it is what you quote in a bug report; it is just not the title.
          */}
          <div className="flex align-items-center gap-2 mb-1 flex-wrap">
            <h1 className="m-0">
              {run.providerName && run.payerName
                ? `${run.providerName} × ${run.payerName}`
                : runDisplayName(run)}
            </h1>
            <Tag
              value={runStatusLabel(run.status)}
              severity={runStatusSeverity(run.status)}
            />
          </div>
          <div className="flex align-items-center gap-2 mb-3 text-sm text-color-secondary flex-wrap">
            {siteTierLabel(run.siteTier) ? (
              <span>{siteTierLabel(run.siteTier)} rates</span>
            ) : null}
            {siteTierLabel(run.siteTier) ? <span>·</span> : null}
            <span title={run.id}>{modelIdLabel(run.id)}</span>
            {run.versionNo > 1 ? (
              <>
                <span>·</span>
                <span>Version {run.versionNo}</span>
              </>
            ) : null}
          </div>
          {/*
            CLE-2811 — a run started from a contract page is bound to that contract: its provider and
            payer came from it, its attachments were seeded as documents, uploads here are attached
            back to it, and "Save to contract" updates it. The page must say so, or nothing does.
          */}
          {run.contractFeeScheduleId ? (
            <Message
              severity="info"
              className="mb-3"
              content={
                <span>
                  <i className="pi pi-link mr-2" />
                  Modeling an existing contract:{" "}
                  <Link
                    to={`/payer-rules/contracts/${run.contractFeeScheduleId}`}
                  >
                    {run.providerName && run.payerName
                      ? `${run.providerName} – ${run.payerName}`
                      : "open contract"}
                  </Link>
                  . Its attachments are this {CONTRACT_MODEL}&apos;s documents,
                  files uploaded here are attached to it, and the contract term
                  you accept is saved onto it — filling empty fields only.
                </span>
              }
            />
          ) : null}

          {/*
            The steering the reviewer gave the model at generation time — "model Network P only", etc.
            Once a model exists the input box is hidden (it only shapes the initial read), so without
            this the instructions would be invisible. It's what tells you which network/slice this run
            represents when one document covers several.

            CLE-2811 — once the conversation panel is shown (`showsConversation`, the same gate as the
            ChatLayout below), the instructions are its first bubble, "Initial instructions", so the card
            would say them twice. It stays for a run that has instructions but no conversation.
          */}
          {run.generationInstructions && !conversationShown ? (
            <div className="border-1 surface-border border-round p-3 mb-4 surface-card">
              <div className="text-sm font-medium text-color-secondary mb-1">
                <i className="pi pi-compass mr-2" />
                Instructions given to the AI
              </div>
              <div className="text-sm white-space-pre-wrap">
                {run.generationInstructions}
              </div>
            </div>
          ) : null}

          {missingInputs.length > 0 || effectiveDateTerms.length > 0 ? (
            <MissingInputsPanel
              items={missingInputs}
              effectiveDateTerms={effectiveDateTerms}
            />
          ) : null}

          <div className="surface-card border-1 surface-border border-round p-3 mb-4">
            <div className="flex align-items-center justify-content-between mb-2">
              <span className="font-semibold">
                Documents ({run.documents.length})
              </span>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept={UPLOAD_ACCEPT}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                  e.target.value = "";
                }}
              />
              <Button
                // A finished run is no longer locked. Ani asked for exactly this: while reviewing the
                // inpatient step she realises she has another document, and she must be able to add it
                // without throwing the run away. It does NOT auto-regenerate — the run just goes stale
                // and the Regenerate bar appears.
                label={uploading ? "Uploading…" : "Upload document"}
                icon="pi pi-upload"
                className="p-button-outlined p-button-sm"
                loading={uploading}
                disabled={uploading || isProcessing(run.status)}
                onClick={() => fileInputRef.current?.click()}
              />
            </div>

            {run.documents.length === 0 && !uploading ? (
              <p className="text-700 text-sm m-0">
                Add the signed contract and any rate workbooks it refers to.
                PDF, Word or Excel.
                {run.contractFeeScheduleId
                  ? " This contract has no attachments yet — anything you upload here is attached to it as well."
                  : ""}
              </p>
            ) : (
              <ul className="list-none p-0 m-0">
                {run.documents.map((d) => (
                  <li key={d.id} className="text-sm py-1">
                    <i className="pi pi-file mr-2 text-700" />
                    {d.fileName}
                  </li>
                ))}
                {uploadingFile ? (
                  <li className="text-sm py-1 text-700">
                    <i className="pi pi-spin pi-spinner mr-2" />
                    Uploading {uploadingFile}…
                  </li>
                ) : null}
              </ul>
            )}

            {/*
              Only before anything has been produced (`showsGenerateRow`). Hidden while generating: the
              progress strip is the thing to look at. CLE-2811 — a contract run whose attachments were
              seeded arrives `queued` (the server starts it), so it never shows this row; neither does a
              run with a model (Regenerate is in the conversation) or a failed one (Retry is).
            */}
            {generateRowShown ? (
              <div className="mt-3">
                {/*
                  Steering for the model, before it runs. This is how ONE contract covering several plan
                  networks / LOBs becomes several runs: each run says which slice it wants. Optional, and
                  general — it also takes "the office column is the one we want", "ignore the behavioral-
                  health exhibit". It only shapes the initial read; after that, corrections go through the
                  conversation.
                */}
                <div className="mb-3">
                  <label
                    htmlFor="gen-instructions"
                    className="block text-sm font-medium mb-1"
                  >
                    Instructions for the AI{" "}
                    <span className="text-color-secondary font-normal">
                      (optional)
                    </span>
                  </label>
                  <InputTextarea
                    id="gen-instructions"
                    className="w-full"
                    autoResize
                    rows={2}
                    disabled={generating || uploading}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="e.g. Model Network P only. Use the office column, not facility."
                  />
                  <small className="text-color-secondary">
                    Scope this {CONTRACT_MODEL} when one document covers several
                    networks or lines of business. The AI follows this over its
                    own reading of the document.
                  </small>
                </div>

                <div className="flex align-items-center gap-2">
                  <Button
                    label="Generate model"
                    icon="pi pi-bolt"
                    disabled={
                      generating || uploading || run.documents.length === 0
                    }
                    onClick={() =>
                      runGeneration({ runId: run.id, instructions })
                    }
                  />
                  {hint ? (
                    <span className="text-700 text-sm">{hint}</span>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          {/* While generating, the wizard below fills in — don't also say "nothing yet". */}
          {generateRowShown ? (
            <p className="text-700">
              Nothing modeled yet. Add the contract documents above, then
              generate.
            </p>
          ) : null}
          {/*
            A facts pass that came back with no contract term has no wizard to host the button, and
            generating again would only re-run the facts pass. Rates are still worth asking for.
          */}
          {/*
            CLE-2811 (2026-09-28) — hidden with the rest of the rates stage (`ratesStageEntries`) until its
            own ticket brings it back.
          */}
          {rateEntries.continueToRates &&
          run.recommendations.length === 0 &&
          (run.status === "completed" || run.status === "awaiting_input") ? (
            <ContinueToRatesCta
              status={run.status}
              continuing={continuing}
              onContinue={handleContinueToRates}
            />
          ) : null}

          {/*
            CLE-2811 — the same layout while generating as after: the steps fill in as the worker reads,
            under a slim progress strip, and the conversation streams the AI's reply beside them.
          */}
          {conversationShown && liveRun ? (
            <ChatLayout className="mt-2">
              <ChatMain>
                {/*
                  CLE-2811 (in place) — not during a FACTS revision pass (a facts run that already has a model): the
                  cards stay as they are and the conversation's live reply says what the pass is doing, with Stop. A
                  full-stage pass (Continue to rates, Regenerate at full) keeps the strip (`inPlaceRevision`).
                */}
                {isProcessing(run.status) && !inPlaceRevision(liveRun) ? (
                  <GenerationProgress
                    run={liveRun}
                    onStop={handleStopPass}
                    stopping={stoppingPass}
                  />
                ) : null}
                {/*
                  CLE-2811 — a failed or dead pass that left nothing to review: no wizard of empty steps,
                  just a line; the conversation beside it says why and offers Retry.
                */}
                {allRecs.length === 0 && !passRunning ? (
                  <p className="text-700 mt-0">{RUN_NOTHING_MODELED}</p>
                ) : (
                  <div className="grid">
                    <div className="col-12">
                      {/*
                        CLE-2811 — not in the facts-first window: the strip says what the pass is doing,
                        and this bar's "your confirmations will be carried over" is not true there — a
                        schedule arriving sends a confirmed term back for re-confirmation.
                      */}
                      {/* CLE-2811 (in place) — nor during a revision pass: its progress is in the conversation, and
                          the cards beside it stay as they are ("the left part stays as it is"). */}
                      {interim || inPlaceRevision(liveRun) ? null : (
                        <PendingChangesBar
                          run={run}
                          regenerating={regenerating}
                          onRegenerate={handleRegenerate}
                        />
                      )}
                    </div>
                    {providerHint && providerHint.otherProviders.length > 0 ? (
                      <div className="col-12">
                        <div
                          className="border-round p-3 border-1 text-sm"
                          style={{
                            background: "var(--yellow-50, #fffbeb)",
                            borderColor: "var(--yellow-300, #fcd34d)",
                          }}
                        >
                          <div className="flex align-items-start gap-2">
                            <i
                              className="pi pi-exclamation-triangle mt-1"
                              style={{ color: "var(--yellow-700, #a16207)" }}
                            />
                            <div className="line-height-3">
                              <span className="font-semibold">
                                This contract looks modeled under a different
                                provider.
                              </span>{" "}
                              Nothing is in production for{" "}
                              <strong>
                                {run.providerName ?? "this provider"}
                              </strong>
                              {run.payerName ? ` + ${run.payerName}` : ""}, but{" "}
                              {providerHint.otherProviders.length === 1
                                ? "this contract already exists under "
                                : "this contract already exists under these providers: "}
                              {providerHint.otherProviders
                                .map((p) => p.name)
                                .join(", ")}
                              . If that&rsquo;s the right entity, start a new{" "}
                              {CONTRACT_MODEL} there &mdash; verification is
                              scoped to this {CONTRACT_MODEL}&rsquo;s provider,
                              so it will keep reading as &ldquo;not in
                              production&rdquo; here.
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                    {/* CLE-2811 — a 200px rail; the wizard takes the rest (the conversation is the wide part). */}
                    <StepRailColumn className="col-12 md:col-3">
                      <StepRail
                        recs={run.recommendations}
                        activeStep={activeStep}
                        onSelect={setActiveStep}
                        unlockedThrough={unlockedThrough}
                        visible={visible}
                        lockedRow={rateEntries.lockedRow}
                        building={building}
                      />
                    </StepRailColumn>

                    <WizardColumn className="col-12 md:col-9">
                      {activeStep === "changes" && (
                        <ChangesScreen run={run} onOpen={handleViewDetails} />
                      )}

                      {activeStep === "build" && (
                        <ImplementationChecklist
                          recs={run.recommendations}
                          verification={verification}
                          loadingLive={loadingLive}
                          onRefresh={refetchVerification}
                          onToggle={handleToggleImplemented}
                          onViewDetails={handleViewDetails}
                          resolvePayer={resolvePayer}
                        />
                      )}

                      {activeStep !== "build" && activeStep !== "changes" && (
                        <>
                          <h3 className="mt-0 mb-1">{activeStepMeta.title}</h3>
                          <p className="mt-0 mb-3 text-700">
                            {activeStepMeta.blurb}
                          </p>

                          {/*
                            CLE-2811 (2026-09-28) — Ani: "In AI modeling I also need to see my notes." What
                            the contract this run models holds now: her notes, and what is saved on it.
                          */}
                          {activeStep === "terms" && run.contractOnFile ? (
                            <ContractOnFileCard contract={run.contractOnFile} />
                          ) : null}

                          {/*
                          CLE-2811 — a first pass fills the step in as the worker reads (plans, each plan's
                          terms), else skeletons. Its recommendations replace this in place when it ends.
                        */}
                          {stepPreviewing ? (
                            <GenerationStepPreview
                              step={activeStep}
                              preview={preview}
                            />
                          ) : null}

                          {activeStep === "segments" && !stepPreviewing && (
                            <LineOfBusinessPicker
                              segments={segments}
                              selected={
                                selectedPayers ?? segments.map((g) => g.key)
                              }
                              busy={busy}
                              onToggle={handleTogglePayer}
                              onApply={handleApplySegments}
                            />
                          )}

                          {activeStep === "service_lines" &&
                            !stepPreviewing && (
                              <ServiceLinePicker
                                recs={run.recommendations}
                                selected={selectedLines ?? availableLines}
                                busy={busy}
                                onToggle={handleToggleServiceLine}
                                onApply={handleApplyServiceLines}
                              />
                            )}

                          {activeStep !== "service_lines" &&
                            activeStep !== "segments" &&
                            !stepPreviewing &&
                            stepRecs.length === 0 && (
                              <p className="text-700">
                                Nothing in this step — the contract didn&rsquo;t
                                produce any {activeStepMeta.title.toLowerCase()}
                                .
                              </p>
                            )}

                          {/*
                      Inpatient and outpatient read as a RATE GRID, not as cards (Ani's v0 steps 2-3).

                      These two steps are where a facility contract's rate sheet lives, and a rate sheet
                      is a table: one row per priced line, in the contract's own vocabulary (revenue
                      codes, DRGs), so you can see at a glance that two lines disagree about methodology
                      or that a carve-out is missing. Rendered as cards, a 16-line outpatient grid is 16
                      things to page through and none of that is visible.

                      Every other step keeps the per-payer card accordion below — a contract term or an
                      MPDR ruleset is a thing you read in full, not a row you scan.
                    */}
                          {isRulesStep && stepRecs.length > 0 && (
                            <AdjustmentRulesPanel
                              recs={stepRecs}
                              busy={busy}
                              focusRecId={focusRecId}
                              onAccept={(rec) => handleAccept(rec.id)}
                              onReject={(rec) => handleReject(rec.id)}
                              renderDetail={(rec) => (
                                <RecommendationCard
                                  rec={rec}
                                  busy={busy}
                                  resolve={resolveIdName}
                                  payers={payerOptions}
                                  baseRateFiles={baseRateFileNames}
                                  baseRateFileOptions={baseRateFileOptions}
                                  existingBaseRateFiles={existingBaseRateFiles}
                                  match={
                                    verification ? verification[rec.id] : null
                                  }
                                  focused={focusRecId === rec.id}
                                  onGenerateCsv={handleGenerateCsv}
                                  generatingCsv={generatingCsvId === rec.id}
                                  onAccept={(edited) =>
                                    handleAccept(rec.id, edited)
                                  }
                                  onReject={() => handleReject(rec.id)}
                                  onOverride={() => setOverrideTarget(rec)}
                                />
                              )}
                            />
                          )}

                          {isGridStep && stepRecs.length > 0 && (
                            <RateGrid
                              recs={stepRecs}
                              focusRecId={focusRecId}
                              renderDetail={(rec) => (
                                <RecommendationCard
                                  rec={rec}
                                  busy={busy}
                                  resolve={resolveIdName}
                                  payers={payerOptions}
                                  baseRateFiles={baseRateFileNames}
                                  baseRateFileOptions={baseRateFileOptions}
                                  existingBaseRateFiles={existingBaseRateFiles}
                                  match={
                                    verification ? verification[rec.id] : null
                                  }
                                  focused={focusRecId === rec.id}
                                  onGenerateCsv={handleGenerateCsv}
                                  generatingCsv={generatingCsvId === rec.id}
                                  onAccept={(edited) =>
                                    handleAccept(rec.id, edited)
                                  }
                                  onReject={() => handleReject(rec.id)}
                                  onOverride={() => setOverrideTarget(rec)}
                                />
                              )}
                            />
                          )}

                          {/*
                      Grouped by PAYER, in an accordion.

                      A contract is usually with several payers, and the agent emits per-payer
                      constructs in generation order — so a flat list interleaved Aetna's contract term,
                      the Medicare Advantage link, and Aetna Medicare Advantage's contract term, which
                      read as three unrelated things and left the reviewer wondering whether two
                      identical-looking cards were duplicates. They were not: they were different payers.

                      One group per payer, so "everything for Aetna" is one thing you open.
                    */}
                          {activeStep !== "service_lines" &&
                            !isGridStep &&
                            !isRulesStep &&
                            stepRecs.length > 0 && (
                              <Accordion
                                multiple
                                // Auto-open when there is only one group — making her click to see the only
                                // thing on the page would be silly. With several payers they start closed, so
                                // the step opens as a short, readable index instead of a wall of forms.
                                activeIndex={
                                  stepPayerGroups.length === 1 ? [0] : []
                                }
                              >
                                {stepPayerGroups.map((group) => {
                                  const unreviewed = group.recs.filter(
                                    (r) =>
                                      r.status === "pending" ||
                                      r.status === "needs_input"
                                  ).length;
                                  const needsPick = group.recs.some((r) =>
                                    needsChoiceIds.includes(r.id)
                                  );
                                  return (
                                    <AccordionTab
                                      key={group.key}
                                      header={
                                        <div className="flex align-items-center gap-2 w-full">
                                          <i
                                            className={
                                              group.payerId != null
                                                ? "pi pi-building"
                                                : "pi pi-sitemap"
                                            }
                                          />
                                          <span
                                            className="font-semibold"
                                            title={
                                              group.payerId != null
                                                ? payerIdTitle(group.payerId)
                                                : undefined
                                            }
                                          >
                                            {group.payerId != null
                                              ? group.payerName ?? UNKNOWN_PAYER
                                              : "Contract-wide (all payers)"}
                                          </span>
                                          <span className="text-color-secondary text-sm">
                                            {group.recs.length}{" "}
                                            {group.recs.length === 1
                                              ? "item"
                                              : "items"}
                                          </span>
                                          {needsPick ? (
                                            <Tag
                                              className="ml-auto"
                                              severity="warning"
                                              icon="pi pi-exclamation-triangle"
                                              value={PICK_A_CONTRACT}
                                            />
                                          ) : null}
                                          {/* eslint-disable-next-line no-nested-ternary */}
                                          {needsPick ? null : unreviewed > 0 ? (
                                            <Tag
                                              className="ml-auto"
                                              severity="warning"
                                              value={`${unreviewed} to review`}
                                            />
                                          ) : (
                                            <Tag
                                              className="ml-auto"
                                              severity="success"
                                              value="Reviewed"
                                            />
                                          )}
                                        </div>
                                      }
                                    >
                                      {group.recs.map((rec) => (
                                        <RecommendationCard
                                          key={rec.id}
                                          rec={rec}
                                          busy={busy}
                                          resolve={resolveIdName}
                                          payers={payerOptions}
                                          baseRateFiles={baseRateFileNames}
                                          baseRateFileOptions={
                                            baseRateFileOptions
                                          }
                                          existingBaseRateFiles={
                                            existingBaseRateFiles
                                          }
                                          match={
                                            verification
                                              ? verification[rec.id]
                                              : null
                                          }
                                          focused={focusRecId === rec.id}
                                          onGenerateCsv={handleGenerateCsv}
                                          generatingCsv={
                                            generatingCsvId === rec.id
                                          }
                                          onAccept={(edited) =>
                                            handleAccept(rec.id, edited)
                                          }
                                          onReject={() => handleReject(rec.id)}
                                          onOverride={() =>
                                            setOverrideTarget(rec)
                                          }
                                          contractOnFile={
                                            run.contractOnFile ?? null
                                          }
                                          onDraftChange={handleDraftChange}
                                          attention={attentionFor(rec)}
                                          scheduleExtraction={
                                            rec.componentType ===
                                            "contract_term"
                                              ? {
                                                  status: run.status,
                                                  interimAt:
                                                    run.interimAt ?? null,
                                                  planStatus: interim
                                                    ? planStatusFor(
                                                        rec,
                                                        liveProgress
                                                      )
                                                    : null,
                                                }
                                              : null
                                          }
                                        />
                                      ))}
                                    </AccordionTab>
                                  );
                                })}
                              </Accordion>
                            )}

                          {/*
                      CLE-2811 — the only control in this tool that writes to a production pricing
                      table. It lives on the Contract Terms step because that is the recommendation
                      it writes, and it previews before it writes.
                    */}
                          {activeStep === "terms" &&
                            !stepPreviewing &&
                            (acceptedContractTerms.length > 0
                              ? acceptedContractTerms
                              : [null]
                            ).map((term) => (
                              <ApplyContractTermPanel
                                // Remount per recommendation: a preview computed for the term BEFORE a
                                // regenerate must not stay on screen, enabling Save, for the term after it.
                                // Within one recommendation the panel drops a preview itself once the values
                                // it was computed for change (`proposedModeling`, keyed by `forValues`).
                                key={term?.id ?? "none"}
                                runId={id!}
                                recommendationId={term?.id ?? null}
                                proposedModeling={
                                  term?.proposedModeling ?? null
                                }
                                contractFeeScheduleId={
                                  run.contractFeeScheduleId
                                }
                                termPayerName={term?.payerName ?? null}
                                attention={
                                  term && needsChoiceIds.includes(term.id)
                                    ? pickContractAttention(term.payerName)
                                    : null
                                }
                              />
                            ))}

                          {/*
                      CLE-2811 — Ani's rhythm: facts first, rates when she asks. A facts run stops at
                      this step; this is the ask.
                    */}
                          {rateEntries.continueToRates &&
                            activeStep === "terms" &&
                            !stepPreviewing && (
                              <ContinueToRatesCta
                                status={run.status}
                                continuing={continuing}
                                onContinue={handleContinueToRates}
                              />
                            )}

                          <div className="flex justify-content-between align-items-center mt-4 pt-3 border-top-1 surface-border">
                            <Button
                              label="Back"
                              icon="pi pi-arrow-left"
                              className="p-button-text"
                              disabled={prevStep === null}
                              onClick={() =>
                                prevStep && setActiveStep(prevStep)
                              }
                            />
                            <div className="flex align-items-center gap-3">
                              {unresolvedHere > 0 ? (
                                <small className="text-color-secondary">
                                  {unresolvedHere} still to review
                                </small>
                              ) : null}
                              {/*
                                CLE-2811 (2026-09-28) — Ani: "It should be Save Draft." With the rates stage
                                hidden, a facts run's last step ends in Save draft (`handleSaveDraft`)
                                rather than a Confirm & continue with nowhere to go.
                              */}
                              {rateEntries.saveDraft && nextStep === null ? (
                                <Button
                                  label={SAVE_DRAFT}
                                  icon="pi pi-save"
                                  loading={savingDraft}
                                  disabled={
                                    savingDraft ||
                                    passRunning ||
                                    saveableTermCount === 0
                                  }
                                  tooltip={SAVE_DRAFT_TOOLTIP}
                                  tooltipOptions={{ position: "top" }}
                                  onClick={handleSaveDraft}
                                />
                              ) : (
                                <Button
                                  // Gated on `pending` only (Ani's v0). A `needs_input` item cannot be built
                                  // until someone fixes data the agent got wrong — blocking on it would freeze
                                  // the run with no way past, so it stays loud rather than becoming a dead end.
                                  label="Confirm & continue"
                                  icon="pi pi-arrow-right"
                                  iconPos="right"
                                  disabled={
                                    nextStep === null || currentStepBlocks
                                  }
                                  tooltip={
                                    currentStepBlocks
                                      ? "Accept, reject or give feedback on every item in this step first"
                                      : undefined
                                  }
                                  onClick={() =>
                                    nextStep && setActiveStep(nextStep)
                                  }
                                />
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </WizardColumn>
                  </div>
                )}
              </ChatMain>
              <ConversationPanel
                run={liveRun}
                activeStep={activeStep}
                regenerating={regenerating}
                onRegenerate={handleRegenerate}
                onDelete={handleDeleteInstruction}
                onSend={(body, stepId) => handleChat(body, stepId)}
                onRetry={handleRetry}
                retrying={retrying}
                onStop={handleStopPass}
                stopping={stoppingPass}
              />
            </ChatLayout>
          ) : null}
        </>
      )}

      {/*
        A recommendation-scoped message. It joins the SAME conversation as the panel — there is one
        mechanism, not two — and is sent straight away (or queued behind a pass that is running).
      */}
      <Dialog
        header={`Request a change to this ${
          overrideTarget ? componentTypeLabel(overrideTarget.componentType) : ""
        }`}
        visible={!!overrideTarget}
        style={{ width: "40rem" }}
        onHide={() => setOverrideTarget(null)}
      >
        <div className="flex flex-column gap-3">
          <p className="text-700 text-sm m-0">
            Tell the AI what it got wrong. This is sent to the{" "}
            <strong>conversation</strong>, tied to this recommendation: the AI
            rebuilds the model and replies there. {REQUEST_CHANGE_QUEUED_HINT}
          </p>
          <p className="text-600 text-sm m-0">
            If you already know the right value, it is quicker to just edit the
            fields and press Confirm.
          </p>
          <InputTextarea
            id="rec-instruction"
            className="w-full"
            rows={4}
            autoResize
            placeholder="e.g. This should be a case rate, not a per diem — see the Rate Schedule on page 12."
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
          />
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setOverrideTarget(null)}
            />
            <Button
              label="Send to the AI"
              icon="pi pi-send"
              disabled={busy || !overrideReason.trim()}
              onClick={submitOverride}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
